import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { vetMedicines } from '../data/vetMedicines';
import { humanMedicines } from '../data/humanMedicines';
import Tesseract from 'tesseract.js';
import { apiUrl } from '../api';

const numberToWords = (num) => {
  if (num === 0) return "ZERO";
  const a = ['', 'ONE ', 'TWO ', 'THREE ', 'FOUR ', 'FIVE ', 'SIX ', 'SEVEN ', 'EIGHT ', 'NINE ', 'TEN ', 'ELEVEN ', 'TWELVE ', 'THIRTEEN ', 'FOURTEEN ', 'FIFTEEN ', 'SIXTEEN ', 'SEVENTEEN ', 'EIGHTEEN ', 'NINETEEN '];
  const b = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  const inWords = (n) => {
    let str = '';
    if (n > 99) { str += a[Math.floor(n / 100)] + 'HUNDRED '; n %= 100; }
    if (n > 19) { str += b[Math.floor(n / 10)] + ' '; n %= 10; }
    if (n > 0) { str += a[n]; }
    return str.trim();
  };
  let result = '';
  if (num >= 10000000) { result += inWords(Math.floor(num / 10000000)) + ' CRORE '; num %= 10000000; }
  if (num >= 100000) { result += inWords(Math.floor(num / 100000)) + ' LAKH '; num %= 100000; }
  if (num >= 1000) { result += inWords(Math.floor(num / 1000)) + ' THOUSAND '; num %= 1000; }
  if (num > 0) { result += inWords(num); }
  return result.trim() + " ONLY";
};

export const parseFreeUnits = (freeVal, schVal) => {
  if (typeof freeVal === 'number' && !isNaN(freeVal) && freeVal > 0) return freeVal;
  const raw = freeVal || schVal;
  if (!raw) return 0;
  const str = String(raw).trim();
  if (str.includes('+')) {
    const parts = str.split('+');
    const freePart = parseFloat(parts[parts.length - 1]);
    if (!isNaN(freePart) && freePart > 0) return freePart;
  }
  const num = parseFloat(str);
  return !isNaN(num) && num > 0 ? num : 0;
};

export const computeRowCostRate = (row) => {
  if (!row) return 0;
  const qty = parseFloat(row.qty) || 0;
  const rate = parseFloat(row.rate) || 0;
  const discVal = parseFloat(row.disc) || 0;
  const freeQty = parseFreeUnits(row.freeQty, row.sch);
  const totalUnits = qty + freeQty;

  if (rate <= 0) return 0;

  // Direct amount deduction from row amount (or % if string contains %)
  const baseAmount = qty * rate;
  let discAmt = 0;
  if (discVal > 0) {
    if (typeof row.disc === 'string' && row.disc.includes('%')) {
      discAmt = (baseAmount * parseFloat(row.disc)) / 100;
    } else {
      discAmt = Math.min(baseAmount, discVal);
    }
  }

  const netRowAmount = Math.max(0, baseAmount - discAmt);
  const discountedRate = qty > 0 ? (netRowAmount / qty) : 0;

  // Row GST: if explicitly defined >= 0 use it; if missing or undefined, default to 0% (or item GST)
  const parsedGst = parseFloat(row.gst);
  const gstVal = (!isNaN(parsedGst) && parsedGst >= 0) ? parsedGst : 0;

  const rateWithGst = discountedRate * (1 + gstVal / 100);

  if (qty > 0 && totalUnits > qty) {
    return Math.round(((qty * rateWithGst) / totalUnits) * 100) / 100;
  }

  return Math.round(rateWithGst * 100) / 100;
};

const Purchases = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceSearchInputRef = useRef(null);
  const [selectedBillIndex, setSelectedBillIndex] = useState(0);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPastBillsDrawer, setShowPastBillsDrawer] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [editingBillId, setEditingBillId] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplier, setNewSupplier] = useState({ name: '', address: '', phone: '', gst: '' });
  const [allPurchases, setAllPurchases] = useState([]);
  
  const [localMedicines, setLocalMedicines] = useState(() => {
    const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
    const vetList = vetMedicines.map(m => ({ ...m, category: m.category || 'Vet Medicine' }));
    const humanList = humanMedicines.map(m => ({ ...m, category: m.category || 'Human Medicine' }));
    return [...vetList, ...humanList, ...custom].sort((a, b) => a.name.localeCompare(b.name));
  });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [activeProductSearchRow, setActiveProductSearchRow] = useState(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSuggestionIndex, setProductSuggestionIndex] = useState(0);
  const [selectedSearchCategory, setSelectedSearchCategory] = useState('ALL');

  const [activeProductRowId, setActiveProductRowId] = useState(null);
  const [newProduct, setNewProduct] = useState({ name: '', company: '', major: '', strip: '', gst: '', category: 'Human Medicine' });
  
  const [activeProductDetailsRow, setActiveProductDetailsRow] = useState(null);

  // Edit Product Modal States
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProductOriginalName, setEditingProductOriginalName] = useState('');
  const [editProductData, setEditProductData] = useState({
    name: '',
    company: '',
    category: 'Human Medicine',
    packing: '',
    mrp: '',
    rate: '',
    gst: '0'
  });
  const [editProductSearchTerm, setEditProductSearchTerm] = useState('');

  const openEditProductModal = (productName) => {
    let targetName = (productName || '').trim();
    if (!targetName && selectedRowId) {
      const activeRow = rows.find(r => r.id === selectedRowId);
      if (activeRow && activeRow.productName?.trim()) {
        targetName = activeRow.productName.trim();
      }
    }
    
    if (targetName) {
      const existing = localMedicines.find(m => m.name.toUpperCase() === targetName.toUpperCase()) ||
                       rows.find(r => r.productName?.toUpperCase() === targetName.toUpperCase());
      setEditingProductOriginalName(targetName);
      setEditProductSearchTerm(targetName);
      setEditProductData({
        name: existing?.name || targetName,
        company: existing?.company || existing?.mfg || '',
        category: existing?.category || 'Human Medicine',
        packing: existing?.packing || existing?.strip || '',
        mrp: existing?.mrp !== undefined && existing?.mrp !== '' ? String(existing.mrp) : '',
        rate: existing?.rate !== undefined && existing?.rate !== '' ? String(existing.rate) : (existing?.purchaseRate ? String(existing.purchaseRate) : ''),
        gst: existing?.gst !== undefined && existing?.gst !== '' ? String(existing.gst) : '0'
      });
    } else {
      setEditingProductOriginalName('');
      setEditProductSearchTerm('');
      setEditProductData({
        name: '',
        company: '',
        category: 'Human Medicine',
        packing: '',
        mrp: '',
        rate: '',
        gst: '0'
      });
    }
    setShowEditProductModal(true);
  };

  const handleUpdateProduct = () => {
    const trimmedName = (editProductData.name || '').trim();
    if (!trimmedName) {
      alert("Product Name is required!");
      return;
    }

    const updatedProd = {
      name: trimmedName,
      company: (editProductData.company || '').trim(),
      category: editProductData.category || 'Human Medicine',
      packing: (editProductData.packing || '').trim(),
      mrp: parseFloat(editProductData.mrp) || 0,
      rate: parseFloat(editProductData.rate) || 0,
      gst: parseFloat(editProductData.gst) || 0
    };

    try {
      let custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
      const origUpper = (editingProductOriginalName || trimmedName).toUpperCase().trim();
      const existingIdx = custom.findIndex(m => m.name.toUpperCase().trim() === origUpper);
      if (existingIdx !== -1) {
        custom[existingIdx] = { ...custom[existingIdx], ...updatedProd };
      } else {
        custom.push(updatedProd);
      }
      localStorage.setItem('customMedicines', JSON.stringify(custom));

      // Update localMedicines list in state
      setLocalMedicines(prev => {
        const next = [...prev];
        const idx = next.findIndex(m => m.name.toUpperCase().trim() === origUpper);
        if (idx !== -1) {
          next[idx] = { ...next[idx], ...updatedProd };
        } else {
          next.push(updatedProd);
        }
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });

      // Update matching rows in current purchase bill
      setRows(prevRows => prevRows.map(r => {
        if (r.productName?.toUpperCase().trim() === origUpper) {
          const newGst = updatedProd.gst || r.gst;
          const newRate = updatedProd.rate > 0 ? updatedProd.rate : r.rate;
          const newMrp = updatedProd.mrp > 0 ? updatedProd.mrp : r.mrp;
          const costVal = computeRowCostRate({ ...r, rate: newRate, gst: newGst });
          return {
            ...r,
            productName: updatedProd.name,
            mfg: updatedProd.company || r.mfg,
            packing: updatedProd.packing || r.packing,
            mrp: newMrp,
            rate: newRate,
            costRate: costVal,
            costPrice: costVal,
            gst: newGst
          };
        }
        return r;
      }));

      setShowEditProductModal(false);
      alert(`✅ Product "${updatedProd.name}" updated successfully!`);
    } catch (e) {
      console.error("Error updating product:", e);
      alert("Failed to update product: " + e.message);
    }
  };

  const selectProduct = (m, rowId) => {
    setSelectedRowId(rowId);
    updateRow(rowId, 'productName', m.name);
    updateRow(rowId, 'qty', 1);
    if (m.company) updateRow(rowId, 'mfg', m.company);
    
    // First, check if the product was already added in the current bill (rows)
    let foundInCurrent = false;
    const existingRow = [...rows].reverse().find(r => r.id !== rowId && r.productName?.toUpperCase() === m.name.toUpperCase());
    
    if (existingRow) {
      if (existingRow.packing) updateRow(rowId, 'packing', existingRow.packing);
      if (existingRow.mrp) updateRow(rowId, 'mrp', existingRow.mrp);
      if (existingRow.rate) updateRow(rowId, 'rate', existingRow.rate);
      if (existingRow.gst) updateRow(rowId, 'gst', existingRow.gst);
      if (existingRow.disc) updateRow(rowId, 'disc', existingRow.disc);
      if (existingRow.scmDisc) updateRow(rowId, 'scmDisc', existingRow.scmDisc);
      if (existingRow.scmDiscAmt) updateRow(rowId, 'scmDiscAmt', existingRow.scmDiscAmt);
      foundInCurrent = true;
    }

    // Fallback to auto-fill MRP, Rate, etc. from last purchase if available
    if (!foundInCurrent && allPurchases) {
      for (let i = 0; i < allPurchases.length; i++) {
        const bill = allPurchases[i];
        const item = (bill.items || []).find(it => it.productName?.toUpperCase() === m.name.toUpperCase());
        if (item) {
          if (item.packing) updateRow(rowId, 'packing', item.packing);
          if (item.mrp) updateRow(rowId, 'mrp', item.mrp);
          if (item.rate) updateRow(rowId, 'rate', item.rate);
          if (item.gst) updateRow(rowId, 'gst', item.gst);
          if (item.disc) updateRow(rowId, 'disc', item.disc);
          if (item.scmDisc) updateRow(rowId, 'scmDisc', item.scmDisc);
          if (item.scmDiscAmt) updateRow(rowId, 'scmDiscAmt', item.scmDiscAmt);
          foundInCurrent = true;
          break;
        }
      }
    }

    if (!foundInCurrent) {
      if (m.strip || m.packing) updateRow(rowId, 'packing', m.strip || m.packing);
      if (m.mrp) updateRow(rowId, 'mrp', m.mrp);
      if (m.rate) updateRow(rowId, 'rate', m.rate);
      updateRow(rowId, 'gst', (m.gst !== undefined && m.gst !== null && m.gst !== '') ? parseFloat(m.gst) : 0);
    }

    setActiveProductSearchRow(null);
    setProductSearchQuery('');

    // Instead of focusing the inline input, open the Product Details popup
    setActiveProductDetailsRow(rowId);
  };

  // Filtered bills for past bills drawer (Search by Invoice No, Supplier Name, Date)
  const filteredPastBills = useMemo(() => {
    if (!allPurchases || allPurchases.length === 0) return [];
    const term = (invoiceSearchTerm || '').trim().toLowerCase();
    if (!term) return allPurchases;
    return allPurchases.filter(bill => {
      const inv = (bill.invoiceNo || '').toLowerCase();
      const sup = (bill.supplierName || '').toLowerCase();
      const dt = (bill.date || '').toLowerCase();
      return inv.includes(term) || sup.includes(term) || dt.includes(term);
    });
  }, [allPurchases, invoiceSearchTerm]);

  // Handle F3 key press strictly for editing purchase bill & drawer keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F3 Key - strictly dedicated for editing purchase bill
      // F2 or Insert key - Add New Product Modal (Standard Marg ERP convention)
      if (e.key === 'F2' || e.code === 'F2' || e.key === 'Insert' || e.which === 113) {
        e.preventDefault();
        e.stopPropagation();
        setActiveProductRowId(null);
        setShowProductModal(true);
        return;
      }

      // F4 key - Edit Product Modal
      if (e.key === 'F4' || e.code === 'F4' || e.which === 115) {
        e.preventDefault();
        e.stopPropagation();
        const activeRow = rows.find(r => r.id === selectedRowId && r.productName?.trim());
        openEditProductModal(activeRow ? activeRow.productName : null);
        return;
      }

      if (e.key === 'F3' || e.code === 'F3' || e.which === 114) {
        e.preventDefault();
        e.stopPropagation();

        setShowPastBillsDrawer(prev => {
          if (!prev) {
            setInvoiceSearchTerm('');
            setSelectedBillIndex(0);
            setTimeout(() => {
              invoiceSearchInputRef.current?.focus();
            }, 60);
            return true;
          } else {
            // If drawer is already open, pressing F3 immediately loads the highlighted bill for editing
            if (filteredPastBills && filteredPastBills.length > 0) {
              const idx = (selectedBillIndex >= 0 && selectedBillIndex < filteredPastBills.length) ? selectedBillIndex : 0;
              loadBillForEditing(filteredPastBills[idx]);
            }
            return false;
          }
        });
        return;
      }

      // Arrow navigation, Enter, and Escape inside the Past Bills Drawer
      if (showPastBillsDrawer) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedBillIndex(prev => Math.min(prev + 1, Math.max(0, filteredPastBills.length - 1)));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedBillIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
          if (filteredPastBills && filteredPastBills.length > 0) {
            e.preventDefault();
            const idx = (selectedBillIndex >= 0 && selectedBillIndex < filteredPastBills.length) ? selectedBillIndex : 0;
            loadBillForEditing(filteredPastBills[idx]);
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setShowPastBillsDrawer(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [showPastBillsDrawer, filteredPastBills, selectedBillIndex]);

  // Auto-load bill for editing if routed with state (from Stockist or Monthly Purchases)
  useEffect(() => {
    if (location.state?.editBillId && allPurchases && allPurchases.length > 0) {
      const billToEdit = allPurchases.find(p => p.id === location.state.editBillId);
      if (billToEdit) {
        loadBillForEditing(billToEdit);
      }
    } else if (location.state?.editBill) {
      loadBillForEditing(location.state.editBill);
    }
  }, [location.state, allPurchases]);

  // Header state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierInputText, setSupplierInputText] = useState('');
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [paymentType, setPaymentType] = useState('Credit');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [vchNo, setVchNo] = useState(1);

  // Document Upload & AI Extraction State
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionStatus, setExtractionStatus] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [isSplitView, setIsSplitView] = useState(false);
  const [splitWidth, setSplitWidth] = useState('28%');
  const [showLeftCard, setShowLeftCard] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [previewModalBill, setPreviewModalBill] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState(localStorage.getItem('gemini_api_key') || '');

  // Synchronize permanent Gemini API key across client sessions
  useEffect(() => {
    const syncPermanentApiKey = async () => {
      try {
        const localKey = localStorage.getItem('gemini_api_key');
        if (!localKey || localKey.trim().length < 5) {
          const res = await fetch(apiUrl('/api/settings/gemini-key'));
          if (res.ok) {
            const data = await res.json();
            if (data.apiKey && data.apiKey.trim().length > 5) {
              localStorage.setItem('gemini_api_key', data.apiKey.trim());
              setApiKeyInput(data.apiKey.trim());
            }
          }
        }
      } catch (e) {
        console.warn('API key sync notice:', e);
      }
    };
    syncPermanentApiKey();
  }, []);

  const rotateImageFile = (fileOrUrl, degrees, maxWidth = 1000, maxHeight = 1000) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const isObjectUrl = typeof fileOrUrl !== 'string';
      const srcUrl = isObjectUrl ? URL.createObjectURL(fileOrUrl) : fileOrUrl;

      img.onload = () => {
        if (isObjectUrl) URL.revokeObjectURL(srcUrl);
        let origW = img.width;
        let origH = img.height;
        if (degrees === 90 || degrees === 270) {
          origW = img.height;
          origH = img.width;
        }
        let width = origW;
        let height = origH;
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);

        const drawW = (degrees === 90 || degrees === 270) ? height : width;
        const drawH = (degrees === 90 || degrees === 270) ? width : height;
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.70);
        resolve({ dataUrl, base64: dataUrl.split(',')[1] });
      };
      img.onerror = (err) => {
        if (isObjectUrl) URL.revokeObjectURL(srcUrl);
        reject(err);
      };
      img.src = srcUrl;
    });
  };

  const extractWithGemini = async (base64Data, mimeType, apiKey) => {
    const prompt = `Extract medical purchase invoice / bill details to JSON with 100% precision:
- supplierName: Distributor / Party / Supplier name printed at the top
- invoiceNo: Invoice or Bill number
- date: Invoice date in YYYY-MM-DD format
- grossAmount: Printed Subtotal or Gross amount before discounts (number, e.g. 3669.37)
- taxableAmount: Printed Taxable Amount / Base Amount BEFORE GST from summary table (e.g. 3342.61). Do NOT confuse this with the Net Bill Total.
- totalTax: Printed Total Tax / GST amount from summary (e.g. 127.68). Look at the tax summary table: sum of CGST + SGST (e.g. 63.84 + 63.84 = 127.68) or the "Add" line in the summary.
- cgstAmount: Printed CGST tax amount from summary table (e.g. 63.84)
- sgstAmount: Printed SGST tax amount from summary table (e.g. 63.84)
- igstAmount: Printed IGST tax amount (number or 0)
- cashDisc: Printed Cash Discount amount from the summary table (e.g. 175.92). Look for row titled "Cash Disc".
- cashDiscPercent: Printed Cash Discount percentage if printed, else compute (cashDisc / grossAmount * 100)
- itemDisc: Printed Item Discount amount from summary table (e.g. 0.00). Look for "Item Disc".
- schDisc: Printed Scheme Discount amount from summary table (e.g. 150.84). Look for "Sch. Disc" or "SCHEME DISC".
- totalDiscount: Total printed discount amount (number or 0)
- lessAmount: Printed "Less" amount from the summary footer (e.g. 326.76).
- preTaxLess: Printed Less amount deducted before GST to arrive at Taxable Amount (e.g. 326.76)
- postTaxLess: Printed Less amount deducted after GST from the Net Bill Total (number or 0)
- lessType: Either "PRE_TAX" (if deducted before GST from subtotal) or "POST_TAX" (if deducted after GST on Net Bill)
- roundOff: Printed Round Off adjustment amount (number or 0, e.g. -0.29)
- totalItems: Total items count printed on bill (number, e.g. 8)
- totalQty: Total quantity count printed on bill (number, e.g. 23)
- netAmount: Printed Net Amount / "To Pay" / Grand Total to pay AFTER GST and Round Off (e.g. 3470.00).
- items: Array of [{ productName, packing, mfg, batch, exp, qty, freeQty, sch, mrp, rate, disc, gst, amount }]

CRITICAL INSTRUCTIONS:
- ORIENTATION (CRITICAL): If the bill image is rotated sideways or upside down, mentally orient it upright first before reading columns.
- EXACT COLUMN MATCHING:
  - Match each cell to its exact column header: QTY, SCH, BATCH, EXP, MRP, RATE, DISC, GST%, AMOUNT.
  - If a column is blank for a row, set it to 0 or blank. Do NOT shift values from other columns or neighboring rows!
  - SCHEME / FREE: If the row has a value in SCH or FREE (e.g. 5 in row 4), put it into "sch" (and "freeQty"). Do NOT put it into "gst" or "disc"!
  - GST / TAX: Read the exact GST% printed for each item (e.g. 0.00, 5.00, 12.00). Items with 0.00% GST must have "gst": 0.
- SUMMARY TOTALS (CRITICAL):
  - In the summary footer, read the exact printed numbers for Cash Disc, Sch. Disc, Gross, Less, Add (Tax), and To Pay.
  - The tax table has exact Taxable, CGST, SGST, and Total Tax amounts. Extract them with 100% precision.
Return strictly valid JSON only with no markdown formatting.`;

    const models = [
      'gemini-3.8-flash',
      'gemini-3.7-flash',
      'gemini-3.5-flash',
      'gemini-flash-latest',
      'gemini-3.5-flash-lite',
      'gemini-flash-lite-latest',
      'gemini-3.6-flash',
      'gemini-3-flash-preview'
    ];
    let lastErr = null;

    for (const model of models) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);

        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [{
              parts: [
                { text: prompt },
                {
                  inline_data: {
                    mime_type: mimeType || 'image/jpeg',
                    data: base64Data
                  }
                }
              ]
            }],
            generationConfig: {
              response_mime_type: 'application/json',
              max_output_tokens: 4096
            }
          })
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errJson = await response.json().catch(() => ({}));
          const errMsg = errJson.error?.message || `HTTP ${response.status}`;
          
          if (errMsg.toLowerCase().includes('api key not valid') || errMsg.toLowerCase().includes('api_key_invalid')) {
            throw new Error('Invalid API Key. Please click "🔑 AI Key" to enter a fresh key from aistudio.google.com.');
          }

          lastErr = new Error(errMsg);
          console.warn(`Model ${model} notice:`, errMsg);
          continue;
        }

        const resJson = await response.json();
        const rawText = resJson.candidates?.[0]?.content?.parts?.[0]?.text;
        if (rawText) {
          const cleanJson = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
          return JSON.parse(cleanJson);
        }
      } catch (e) {
        lastErr = e;
        if (e.message && e.message.includes('Invalid API Key')) {
          throw e;
        }
      }
    }
    throw lastErr || new Error('Unable to read this bill using Gemini AI Vision.');
  };

  const extractPackingFromName = (name, existingPacking = '') => {
    let pack = (existingPacking || '').toUpperCase().trim();
    if (!name) return { cleanName: '', packing: pack };
    let str = name.trim();

    if (pack) {
      if (str.toUpperCase().endsWith(pack)) {
        str = str.substring(0, str.length - pack.length).trim();
      }
      return { cleanName: str.toUpperCase().trim(), packing: pack };
    }

    // Matches multi-pack: e.g. 10X10, 1X10, 10*10, 10X15, 10X10'S, 10X10 TAB, 10X10 CAP
    const multiMatch = str.match(/(?:^|\s|\()(\d+\s*[xX*]\s*\d+(?:'S|\s*TAB|\s*CAP|\s*S)?)(?:\)|\s|$)/i);
    if (multiMatch) {
      pack = multiMatch[1].trim().toUpperCase();
      const cleanName = str.replace(multiMatch[0], ' ').replace(/\s+/g, ' ').trim().toUpperCase();
      return { cleanName, packing: pack };
    }

    // Matches unit packs: e.g. 100ML, 50ML, 200ML, 15'S, 10'S, 20'S, 10TAB, 15CAP, 1VIAL, 2ML, 5ML, 30GM, 50GM, 100 ML, 20 GM, 1 BOTTLE, 1 TUBE
    const unitMatch = str.match(/(?:^|\s|\()(\d+(?:\.\d+)?\s*(?:ML|GM|MG|KG|LTR|TAB|CAP|VIAL|AMP|BOTTLE|TUBE|'S|S|NOS|PCS))(?:\)|\s|$)/i);
    if (unitMatch) {
      pack = unitMatch[1].trim().toUpperCase();
      const cleanName = str.replace(unitMatch[0], ' ').replace(/\s+/g, ' ').trim().toUpperCase();
      return { cleanName, packing: pack };
    }

    return { cleanName: str.toUpperCase().trim(), packing: '' };
  };

  const parseInvoiceTextWithOCR = (ocrText) => {
    if (!ocrText) return null;
    const lines = ocrText.split('\n').map(l => l.trim()).filter(Boolean);
    let supplierName = '';
    let invoiceNo = '';
    let date = new Date().toISOString().split('T')[0];
    const items = [];

    // Filter out header/footer noise and shop address lines
    const noiseKeywords = /TOTAL\s*AMOUNT|SUBTOTAL|GRAND\s*TOTAL|NET\s*PAYABLE|DECLARATION|BANK\s*DETAILS|IFSC\s*CODE|AUTHORISED\s*SIGNATURE|TERMS\s*&\s*CONDITIONS|JURISDICTION|BRANCH\s*NAME|SUMMARY\s*SLIP/i;
    const addressKeywords = /SHOP|FLOOR|GROUND|MILKAT|RADHANAGAR|KOLHAPUR|STATE\s*CODE|FLAT|BUILDING|ROAD|TALUKA|DISTRICT|JURISDICTION|BRANCH|ACCOUNT|BANK|IFSC|PAN|CIN|GSTIN|E\.&O\.E|DECLARATION|AUTHORISED|SIGNATURE|TERMS|PAGI|PAGE|SGST|CGST|IGST|SLIP|MEMO|PAY|PASS|ASYRAL|SCHEME/i;
    const headerRowKeywords = /^(\s*SR|\s*SL|\s*S\.NO|\s*ITEM\s*NAME|\s*PRODUCT\s*NAME|\s*DESCRIPTION|\s*HSN|\s*QTY|\s*RATE|\s*MRP|\s*AMOUNT\s*)$/i;

    // Supplier Name detection: Look for company / party keywords in header lines
    for (let i = 0; i < Math.min(lines.length, 8); i++) {
      const line = lines[i];
      if (/BALAJI|VETGEN|PHARMA|MEDICAL|DISTRIBUTORS|AGENCIES|ENTERPRISES|CHEMIST|DRUG|PVT|LTD|LIMITED/i.test(line) && !/INVOICE|BILL|TAX|MEMO|DATE|NO|SHOP|FLOOR|GROUND/i.test(line)) {
        supplierName = line.toUpperCase().trim();
        break;
      }
    }

    if (!supplierName) {
      for (let i = 0; i < Math.min(lines.length, 5); i++) {
        const line = lines[i];
        if (line.length > 4 && !/INVOICE|BILL|TAX|MEMO|DATE|NO|ITEM|SR|SHOP|FLOOR|GROUND|MILKAT|STATE/i.test(line)) {
          supplierName = line.toUpperCase().trim();
          break;
        }
      }
    }

    for (const line of lines) {
      const invMatch = line.match(/(?:INV|BILL|NO|NUM|#)[:.\s]*([A-Z0-9\-/]+)/i);
      if (invMatch && invMatch[1] && invMatch[1].length >= 3 && !invoiceNo) {
        invoiceNo = invMatch[1];
      }
      const dateMatch = line.match(/(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4})/);
      if (dateMatch && dateMatch[1]) {
        date = dateMatch[1];
      }
    }

    lines.forEach(line => {
      // Skip pure footer / summary lines, shop address lines, or table header rows
      if (noiseKeywords.test(line) || addressKeywords.test(line) || headerRowKeywords.test(line)) return;

      const tokens = line.split(/\s+/);
      const textParts = tokens.filter(t => 
        /^[A-Za-z0-9\-\.\/]+$/.test(t) && 
        !/^(SR|SL|SNO|QTY|FREE|MRP|RATE|DISC|GST|TAX|AMOUNT|BATCH|EXP|EXPIRY|HSN|PACK|PCS|NOS|RS|\d+|\d+\.\d+)$/i.test(t) &&
        !addressKeywords.test(t)
      );
      
      const numbers = tokens
        .map(t => parseFloat(t.replace(/[^0-9.]/g, '')))
        .filter(n => !isNaN(n) && n > 0 && n < 100000);

      if (textParts.length >= 1 && numbers.length >= 1) {
        const name = textParts.join(' ').toUpperCase().trim();
        if (name.length >= 2 && !noiseKeywords.test(name) && !addressKeywords.test(name) && !/^(TOTAL|SUBTOTAL|AMOUNT|NET|BILL|INVOICE|TAX|GST)$/i.test(name)) {
          const rawQty = numbers[0] || 1;
          const rawMrp = numbers.length >= 2 ? numbers[1] : numbers[0];
          const rawRate = numbers.length >= 3 ? numbers[2] : (rawMrp > 0 ? Math.round(rawMrp * 0.8 * 100) / 100 : rawMrp);

          const qty = rawQty > 5000 ? 1 : rawQty;
          const mrp = rawMrp > 50000 ? 0 : rawMrp;
          const rate = rawRate > 50000 ? 0 : rawRate;

          const batchToken = tokens.find(t => /^[A-Z0-9]{4,12}$/i.test(t) && /\d/.test(t) && !addressKeywords.test(t));
          const expToken = tokens.find(t => /^\d{2}[/-]\d{2,4}$/.test(t));
          const { cleanName, packing: ocrPacking } = extractPackingFromName(name);

          items.push({
            productName: cleanName,
            packing: ocrPacking,
            mfg: '',
            batch: batchToken || '',
            exp: expToken || '',
            qty: qty,
            freeQty: 0,
            mrp: mrp,
            rate: rate,
            disc: 0,
            gst: 5.0
          });
        }
      }
    });

    return items.length > 0 ? { supplierName, invoiceNo, date, items } : null;
  };

  const compressImage = (fileOrUrl, maxWidth = 1000, maxHeight = 1000, quality = 0.70) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const isObjectUrl = typeof fileOrUrl !== 'string';
      const srcUrl = isObjectUrl ? URL.createObjectURL(fileOrUrl) : fileOrUrl;

      img.onload = () => {
        if (isObjectUrl) URL.revokeObjectURL(srcUrl);
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        const base64 = dataUrl.split(',')[1];
        resolve({ dataUrl, base64, width, height });
      };
      img.onerror = (err) => {
        if (isObjectUrl) URL.revokeObjectURL(srcUrl);
        reject(err);
      };
      img.src = srcUrl;
    });
  };

  const normalizeExtractedData = (data) => {
    if (!data || typeof data !== 'object') return null;
    const itemsList = data.items || data.products || data.medicines || data.itemList || data.rows || data.billItems || [];
    
    // Determine bill-level GST percentage as reliable fallback for items without explicit GST
    const taxableVal = parseFloat(data.taxableAmount || 0) || 0;
    const taxVal = parseFloat(data.totalTax || data.taxAmount || data.gstAmount || 0) || 0;
    const cgstVal = parseFloat(data.cgstAmount || 0) || 0;
    const sgstVal = parseFloat(data.sgstAmount || 0) || 0;
    const combinedTax = taxVal > 0 ? taxVal : (cgstVal + sgstVal);

    // If bill explicitly specifies 0 tax or totalTax is 0 or no tax is present, default GST should be 0!
    let billGstPercent = 0; // Default to 0 unless specified in bill
    if (combinedTax === 0 && (data.totalTax !== undefined || data.taxAmount !== undefined || data.taxableAmount !== undefined)) {
      billGstPercent = 0;
    } else if (taxableVal > 0 && combinedTax > 0) {
      const computedPct = Math.round((combinedTax / taxableVal) * 100);
      if (computedPct >= 0 && computedPct <= 28) {
        billGstPercent = computedPct;
      }
    } else if (combinedTax > 0) {
      const grossVal = parseFloat(data.grossAmount || data.subtotal || 0) || 0;
      if (grossVal > 0) {
        const computedPct = Math.round((combinedTax / grossVal) * 100);
        if (computedPct >= 0 && computedPct <= 28) {
          billGstPercent = computedPct;
        }
      }
    }

    if (Array.isArray(itemsList) && itemsList.length > 0) {
      const normalizedItems = itemsList.map(item => {
        const rawName = String(item.productName || item.name || item.item || item.description || 'Unknown Product').toUpperCase().trim();
        const rawPacking = String(
          item.packing || item.pack || item.packSize || item.packaging || 
          item.pkg || item.pack_size || item.packing_size || item.uom || 
          item.unit || item.strip || ''
        ).toUpperCase().trim();

        const { cleanName, packing: parsedPacking } = extractPackingFromName(rawName, rawPacking);

        const rawSch = item.sch !== undefined && item.sch !== null ? String(item.sch).trim() : '';
        const freeVal = parseFreeUnits(item.freeQty || item.free || item.scheme, rawSch);
        const finalSch = rawSch || (freeVal > 0 ? String(freeVal) : '');
        const rateVal = parseFloat(item.rate || item.unitRate || item.price || 0) || 0;
        const discVal = parseFloat(item.disc || item.discount || item.discPercent || 0) || 0;
        
        const rawGstField = item.gst !== undefined ? item.gst : (item.gstPercent !== undefined ? item.gstPercent : item.tax);
        let gstVal = parseFloat(rawGstField);
        if (isNaN(gstVal) || rawGstField === null || rawGstField === '' || rawGstField === undefined) {
          gstVal = billGstPercent;
        }
        const qtyVal = parseFloat(item.qty || item.quantity || 1) || 1;
        const costVal = computeRowCostRate({
          qty: qtyVal,
          rate: rateVal,
          disc: discVal,
          gst: gstVal,
          freeQty: freeVal,
          sch: finalSch
        });

        return {
          productName: cleanName,
          packing: parsedPacking,
          mfg: String(item.mfg || item.company || item.manufacturer || '').toUpperCase().trim(),
          batch: String(item.batch || item.batchNo || item.batchNumber || '').toUpperCase().trim(),
          exp: String(item.exp || item.expiry || item.expiryDate || ''),
          qty: qtyVal,
          freeQty: freeVal,
          sch: finalSch,
          mrp: parseFloat(item.mrp || item.mrpPrice || 0) || 0,
          rate: rateVal,
          costRate: costVal,
          costPrice: costVal,
          disc: discVal,
          gst: gstVal,
          amount: parseFloat(item.amount || item.taxableAmount || item.netRate || 0) || 0
        };
      });

      const totalExtractedQty = normalizedItems.reduce((sum, it) => sum + (parseFloat(it.qty) || 0), 0);
      const computedGross = normalizedItems.reduce((sum, it) => sum + ((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0)), 0);
      const cashDiscVal = parseFloat(data.cashDisc || data.cashDiscount || 0) || 0;
      const itemDiscVal = parseFloat(data.itemDisc || data.itemDiscount || 0) || 0;
      const schDiscVal = parseFloat(data.schDisc || data.schemeDisc || data.schemeDiscount || 0) || 0;
      const totalDiscVal = parseFloat(data.totalDiscount || (cashDiscVal + itemDiscVal + schDiscVal)) || 0;

      const rawLess = parseFloat(data.lessAmount || data.less || 0) || 0;
      let preTaxLessVal = parseFloat(data.preTaxLess || 0) || 0;
      let postTaxLessVal = parseFloat(data.postTaxLess || 0) || 0;
      let determinedLessType = data.lessType || '';

      const grossVal = parseFloat(data.grossAmount || data.subtotal || computedGross) || computedGross;
      const taxVal = parseFloat(data.totalTax || data.taxAmount || data.gstAmount || 0) || 0;
      let netVal = parseFloat(data.netAmount || data.totalAmount || data.grandTotal || 0) || 0;
      const taxableVal = parseFloat(data.taxableAmount || 0) || 0;

      // 1. Check if AI mistakenly extracted taxableAmount as netAmount (e.g. netVal ≈ taxableVal while taxVal > 1.0)
      if (taxableVal > 0 && taxVal > 1.0 && netVal > 0 && Math.abs(netVal - taxableVal) < 2.0) {
        netVal = Math.round((taxableVal + taxVal + (parseFloat(data.roundOff || 0))) * 100) / 100;
      }

      // 2. Identify discount & less relations:
      const totalBreakdownDiscs = cashDiscVal + schDiscVal + itemDiscVal;
      if (totalBreakdownDiscs > 0 && rawLess > 0 && Math.abs(rawLess - totalBreakdownDiscs) < 1.0) {
        // rawLess is simply the sum of Cash Disc + Sch Disc + Item Disc (e.g. 326.76 = 175.92 + 150.84)
        preTaxLessVal = rawLess;
        postTaxLessVal = 0;
        determinedLessType = 'PRE_TAX';
      } else if (cashDiscVal > 0 && taxableVal > 0 && Math.abs((grossVal - cashDiscVal) - taxableVal) < 2.0) {
        preTaxLessVal = cashDiscVal;
        if (rawLess > 0 && Math.abs(rawLess - cashDiscVal) < 0.5) {
          postTaxLessVal = 0;
          determinedLessType = 'PRE_TAX';
        } else if (rawLess > 0 && netVal > 0 && Math.abs((taxableVal + taxVal - rawLess) - netVal) < 2.0) {
          postTaxLessVal = rawLess;
          determinedLessType = 'POST_TAX';
        } else {
          postTaxLessVal = 0;
          determinedLessType = 'PRE_TAX';
        }
      } else if (rawLess > 0) {
        if (taxableVal > 0 && Math.abs((grossVal - rawLess) - taxableVal) < 2.0) {
          determinedLessType = 'PRE_TAX';
          preTaxLessVal = rawLess;
          postTaxLessVal = 0;
        } else if (netVal > 0 && Math.abs((taxableVal + taxVal - rawLess) - netVal) < 2.0) {
          determinedLessType = 'POST_TAX';
          postTaxLessVal = rawLess;
          preTaxLessVal = 0;
        } else {
          determinedLessType = 'PRE_TAX';
          preTaxLessVal = rawLess;
          postTaxLessVal = 0;
        }
      } else {
        determinedLessType = 'PRE_TAX';
        preTaxLessVal = totalBreakdownDiscs > 0 ? totalBreakdownDiscs : cashDiscVal;
        postTaxLessVal = 0;
      }

      return {
        supplierName: String(data.supplierName || data.supplier || data.partyName || data.vendor || '').toUpperCase().trim(),
        invoiceNo: String(data.invoiceNo || data.billNo || data.invoiceNumber || '').toUpperCase().trim(),
        date: data.date || data.invoiceDate || data.billDate || new Date().toISOString().split('T')[0],
        grossAmount: grossVal,
        taxableAmount: taxableVal,
        totalTax: taxVal,
        cgstAmount: parseFloat(data.cgstAmount || 0) || 0,
        sgstAmount: parseFloat(data.sgstAmount || 0) || 0,
        igstAmount: parseFloat(data.igstAmount || 0) || 0,
        cashDisc: cashDiscVal,
        cashDiscPercent: parseFloat(data.cashDiscPercent || 0) || 0,
        itemDisc: itemDiscVal,
        schDisc: schDiscVal,
        totalDiscount: totalDiscVal,
        lessAmount: rawLess,
        preTaxLess: preTaxLessVal,
        postTaxLess: postTaxLessVal,
        lessType: determinedLessType || 'PRE_TAX',
        roundOff: parseFloat(data.roundOff || data.roundOffAmount || 0) || 0,
        totalItems: parseFloat(data.totalItems || normalizedItems.length) || normalizedItems.length,
        totalQty: parseFloat(data.totalQty || totalExtractedQty) || totalExtractedQty,
        netAmount: parseFloat(data.netAmount || data.totalAmount || data.grandTotal || data.billAmount || 0) || 0,
        items: normalizedItems
      };
    }
    return null;
  };

  const handleExtractInvoice = async (file, manualRotation = 0) => {
    if (!file || isExtracting) return;

    // Validate supported image formats (JPG, JPEG, PNG, WEBP)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (file.type && !validTypes.includes(file.type.toLowerCase())) {
      alert('Unsupported image format. Please upload a JPG, JPEG, PNG, or WEBP image.');
      return;
    }

    setCurrentFile(file);
    setIsExtracting(true);
    setExtractionStatus('Reading Bill...');

    try {
      let dataUrl = '';
      let base64Data = '';

      if (manualRotation !== 0) {
        setExtractionStatus(`Reading Bill (Rotating ${manualRotation}°)...`);
        const rotated = await rotateImageFile(file, manualRotation);
        dataUrl = rotated.dataUrl;
        base64Data = rotated.base64;
      } else {
        setExtractionStatus('Reading Bill...');
        const compressed = await compressImage(file, 1000, 1000, 0.70);
        dataUrl = compressed.dataUrl;
        base64Data = compressed.base64;
      }

      setUploadedDoc(dataUrl);
      setShowImageModal(true);

      let parsedData = null;
      const userApiKey = localStorage.getItem('gemini_api_key') || process.env.REACT_APP_GEMINI_API_KEY || '';

      // 1. Direct Gemini Vision API first (Fastest & 100% precise: 1-2 seconds)
      const activeKey = (userApiKey && userApiKey.trim().length > 5) 
        ? userApiKey.trim() 
        : (process.env.REACT_APP_GEMINI_API_KEY || 'AQ.Ab8RN6JPZCyMbnUvHsH1z2Qp4GFAgJSvgniJVmXIz8Ac28gTzA');

      setExtractionStatus('Scanning Bill with Gemini AI Vision...');

      try {
        const resObj = await extractWithGemini(base64Data, file.type || 'image/jpeg', activeKey);
        const norm = normalizeExtractedData(resObj);
        if (norm && norm.items && norm.items.length > 0) {
          parsedData = norm;
        }
      } catch (geminiErr) {
        console.warn('Direct Gemini API extraction failed, trying server scanner fallback:', geminiErr);
        try {
          setExtractionStatus('Retrying with backend scanner...');
          const serverRes = await fetch(apiUrl('/api/extract-invoice'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-gemini-api-key': activeKey
            },
            body: JSON.stringify({
              imageBase64: base64Data,
              mimeType: file.type || 'image/jpeg'
            })
          });
          if (serverRes.ok) {
            const serverData = await serverRes.json();
            const norm = normalizeExtractedData(serverData);
            if (norm && norm.items && norm.items.length > 0) {
              parsedData = norm;
            }
          }
        } catch (serverErr) {
          console.warn('Backend server extraction fallback also failed:', serverErr);
        }

        if (!parsedData) {
          console.error('All extraction attempts failed:', geminiErr);
          const errorMsg = 'Unable to scan this bill. Please ensure the bill image is clear and well-lit.';
          setExtractionStatus(errorMsg);
          alert(errorMsg);
          return;
        }
      }

      if (parsedData && parsedData.items && parsedData.items.length > 0) {
        setExtractionStatus('Processing Products...');
        setExtractedData(parsedData);
        setExtractionStatus('Scan Complete!');
        
        // Auto-apply data directly to form rows without requiring extra button click
        applyExtractedData(parsedData);
        alert(`✨ ${parsedData.items.length} items automatically extracted & applied to Purchase Form! Click "👁️ View Bill" anytime to view or edit the bill image.`);
      } else {
        setExtractedData(null);
        setShowImageModal(true);
        const errorMsg = 'Unable to read this bill. Please upload a clearer image or check your Gemini AI Key.';
        setExtractionStatus(errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Extraction error:', err);
      setExtractedData(null);
      setShowImageModal(true);
      const errorMsg = 'Unable to read this bill. Please upload a clearer image or check your Gemini AI Key.';
      setExtractionStatus(errorMsg);
      alert(errorMsg);
    } finally {
      setIsExtracting(false);
    }
  };

  const applyExtractedData = (customData = null) => {
    const dataToApply = customData || extractedData;
    if (!dataToApply) return;

    if (dataToApply.supplierName) {
      setSupplierInputText(dataToApply.supplierName);
      const found = suppliers.find(s => s.name.toUpperCase() === dataToApply.supplierName.toUpperCase());
      setSelectedSupplier(found || { name: dataToApply.supplierName, address: '', gst: '', phone: '' });
    }

    if (dataToApply.invoiceNo) {
      setInvoiceNo(dataToApply.invoiceNo);
    }

    if (dataToApply.date) {
      setDate(dataToApply.date);
    }

    const extGross = (dataToApply.items || []).reduce((sum, it) => {
      const q = parseFloat(it.qty) || 0;
      const rt = parseFloat(it.rate) || 0;
      return sum + (q * rt > 0 ? q * rt : (parseFloat(it.amount) || 0));
    }, 0);
    const extCashDisc = parseFloat(dataToApply.cashDisc || 0);
    const extSchDisc = parseFloat(dataToApply.schDisc || 0);
    const extItemDisc = parseFloat(dataToApply.itemDisc || 0);
    const extLess = parseFloat(dataToApply.lessAmount || 0);
    let extCashDiscPct = parseFloat(dataToApply.cashDiscPercent || 0);

    // 1. Cash Discount
    if (extCashDisc > 0) {
      setCashDisc(String(extCashDisc));
      setIsManualCashDisc(true);
      const computedPct = extCashDiscPct > 0 
        ? extCashDiscPct 
        : (extGross > 0 ? (Math.round((extCashDisc / extGross) * 100 * 10) / 10) : 0);
      setCashDiscPercent(String(computedPct));
    } else if (extCashDiscPct > 0) {
      setCashDiscPercent(String(extCashDiscPct));
      const calcAmt = ((extGross * extCashDiscPct) / 100).toFixed(2);
      setCashDisc(calcAmt);
      setIsManualCashDisc(true);
    } else {
      setCashDiscPercent('0');
      setCashDisc('0.00');
      setIsManualCashDisc(false);
    }

    // 2. Scheme Discount
    if (extSchDisc > 0) {
      setSchDisc(String(extSchDisc));
    } else {
      setSchDisc('0.00');
    }

    // 3. Item Discount
    if (extItemDisc > 0) {
      setItemDisc(String(extItemDisc));
    } else {
      setItemDisc('0.00');
    }

    // 4. Total Less Amount:
    // If extLess is given, use it! If not, sum up cashDisc + schDisc + itemDisc!
    const sumDiscs = extCashDisc + extSchDisc + extItemDisc;
    const finalLess = extLess > 0 ? extLess : sumDiscs;
    setLessAmount(finalLess > 0 ? String(finalLess) : '0.00');

    if (dataToApply.lessType === 'POST_TAX' && dataToApply.postTaxLess > 0) {
      setLessMode('POST_TAX');
      setLessAmount(String(dataToApply.postTaxLess));
      setPostTaxLess('0');
    } else if (dataToApply.lessType === 'BOTH') {
      setLessMode('BOTH');
      setLessAmount(String(dataToApply.preTaxLess || 0));
      setPostTaxLess(String(dataToApply.postTaxLess || 0));
    } else {
      setLessMode('PRE_TAX');
      setLessAmount(String(dataToApply.preTaxLess || extCashDisc || 0));
      setPostTaxLess('0');
    }

    const extTax = parseFloat(dataToApply.totalTax || dataToApply.taxAmount || dataToApply.gstAmount || 0) || 0;
    const extCgst = parseFloat(dataToApply.cgstAmount || 0) || 0;
    const extSgst = parseFloat(dataToApply.sgstAmount || 0) || 0;
    const extTaxSum = extTax > 0 ? extTax : (extCgst + extSgst);

    // Set manualToPay ONLY if it is the genuine Net Payable amount (not mistaken pre-tax taxable amount)
    if (dataToApply.netAmount && parseFloat(dataToApply.netAmount) > 0) {
      const parsedNet = parseFloat(dataToApply.netAmount);
      // Only flag as taxable amount if the bill actually has substantial tax printed and parsedNet matches pre-tax gross-disc
      const isTaxableAmount = extTaxSum > 1.0 && extGross > 0 && Math.abs(parsedNet - (extGross - extCashDisc)) < 2.0;
      if (!isTaxableAmount) {
        setManualToPay(String(parsedNet));
        setIsManualToPay(true);
      } else {
        // Let autoToPay calculate the true Net To Pay (Taxable + GST)
        setIsManualToPay(false);
        setManualToPay('');
      }
    } else {
      setIsManualToPay(false);
      setManualToPay('');
    }

    if (dataToApply.items && dataToApply.items.length > 0) {
      const billTaxable = parseFloat(dataToApply.taxableAmount || 0) || 0;
      const billTax = parseFloat(dataToApply.totalTax || dataToApply.taxAmount || dataToApply.gstAmount || 0) || 0;
      const billCgst = parseFloat(dataToApply.cgstAmount || 0) || 0;
      const billSgst = parseFloat(dataToApply.sgstAmount || 0) || 0;
      const billTaxSum = billTax > 0 ? billTax : (billCgst + billSgst);
      let billGstPercent = 0;
      if (billTaxSum === 0 && (dataToApply.totalTax !== undefined || dataToApply.taxAmount !== undefined || dataToApply.taxableAmount !== undefined)) {
        billGstPercent = 0;
      } else if (billTaxable > 0 && billTaxSum > 0) {
        const pct = Math.round((billTaxSum / billTaxable) * 100);
        if (pct >= 0 && pct <= 28) billGstPercent = pct;
      } else if (billTaxSum > 0) {
        const grossVal = parseFloat(dataToApply.grossAmount || 0) || 0;
        if (grossVal > 0) {
          const pct = Math.round((billTaxSum / grossVal) * 100);
          if (pct >= 0 && pct <= 28) billGstPercent = pct;
        }
      }

      const newRows = dataToApply.items.map((item, idx) => {
        const qty = parseFloat(item.qty) || 1;
        const mrp = parseFloat(item.mrp) || 0;
        let rate = parseFloat(item.rate) || 0;
        const disc = parseFloat(item.disc) || 0;
        let amount = parseFloat(item.amount) || 0;

        // If rate is missing but row amount and qty exist, compute rate
        if (rate === 0 && amount > 0 && qty > 0) {
          rate = Math.round((amount / qty) * 100) / 100;
        } else if (rate === 0 && mrp > 0) {
          rate = Math.round(mrp * 0.8 * 100) / 100;
        }

        const baseAmount = qty * rate;
        let discVal = parseFloat(item.disc) || 0;
        let discAmt = 0;
        if (discVal > 0) {
          if (typeof item.disc === 'string' && item.disc.includes('%')) {
            discAmt = (baseAmount * parseFloat(item.disc)) / 100;
          } else {
            discAmt = Math.min(baseAmount, discVal);
          }
        }
        if (amount === 0) {
          amount = Math.max(0, baseAmount - discAmt);
        }

        let packingVal = (item.packing || '').toUpperCase().trim();
        // If packing is still blank, check if we have it in allPurchases or localMedicines catalog
        if (!packingVal) {
          const matchInPur = (allPurchases || []).flatMap(p => p.items || []).find(it => 
            it.productName?.toUpperCase() === item.productName?.toUpperCase() && it.packing
          );
          if (matchInPur) {
            packingVal = matchInPur.packing.toUpperCase().trim();
          } else {
            const matchInMed = (localMedicines || []).find(m => 
              m.name?.toUpperCase() === item.productName?.toUpperCase() && (m.strip || m.packing)
            );
            if (matchInMed) {
              packingVal = (matchInMed.strip || matchInMed.packing).toUpperCase().trim();
            }
          }
        }

        const rawSch = item.sch !== undefined && item.sch !== null ? String(item.sch).trim() : '';
        const freeVal = parseFreeUnits(item.freeQty || item.free || item.scheme, rawSch);
        const finalSch = rawSch || (freeVal > 0 ? String(freeVal) : '');
        const rawGstField = item.gst !== undefined ? item.gst : (item.gstPercent !== undefined ? item.gstPercent : item.tax);
        let rowGst = parseFloat(rawGstField);
        if (isNaN(rowGst) || rawGstField === null || rawGstField === '' || rawGstField === undefined) {
          rowGst = billGstPercent;
        }

        const costVal = computeRowCostRate({
          qty: qty,
          rate: rate,
          disc: disc,
          gst: rowGst,
          freeQty: freeVal,
          sch: finalSch
        });

        return {
          id: Date.now() + idx + Math.random(),
          productName: (item.productName || '').toUpperCase().trim(),
          packing: packingVal,
          mfg: (item.mfg || '').toUpperCase().trim(),
          batch: (item.batch || '').toUpperCase().trim(),
          exp: item.exp || '',
          qty: qty,
          freeQty: freeVal,
          sch: finalSch,
          mrp: mrp,
          rate: rate,
          costRate: costVal,
          costPrice: costVal,
          disc: disc,
          gst: rowGst,
          amount: Math.round(amount * 100) / 100
        };
      });

      newRows.push(getInitialRow(Date.now() + 99999));
      setRows(newRows);
    }

    setIsSplitView(true);
    setShowImageModal(false);
  };
  

  // Fetch suppliers and purchases on component mount, and aggregate product catalog
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [supRes, purRes] = await Promise.all([
          fetch(apiUrl('/api/suppliers')),
          fetch(apiUrl('/api/purchases'))
        ]);
        let purData = [];
        if (supRes.ok) {
          const supData = await supRes.json();
          setSuppliers(supData);
        }
        if (purRes.ok) {
          purData = await purRes.json();
          setAllPurchases(purData);
          setVchNo(purData.length + 1);
        }

        // Aggregate unique products from vetMedicines, humanMedicines, localStorage customMedicines, and purData
        const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
        const purchasedProducts = purData.flatMap(p => (p.items || []).map(i => ({
          name: (i.productName || '').toUpperCase().trim(),
          company: i.mfg || i.company || '',
          category: i.category || 'General'
        }))).filter(i => i.name);

        const combinedMap = new Map();
        const vetList = vetMedicines.map(m => ({ ...m, category: m.category || 'Vet Medicine' }));
        const humanList = humanMedicines.map(m => ({ ...m, category: m.category || 'Human Medicine' }));

        [...vetList, ...humanList, ...custom, ...purchasedProducts].forEach(m => {
          if (m && m.name) {
            const key = m.name.toUpperCase().trim();
            if (!combinedMap.has(key)) {
              combinedMap.set(key, { 
                name: key, 
                company: m.company || m.mfg || '', 
                category: m.category || 'General' 
              });
            }
          }
        });

        const mergedList = Array.from(combinedMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        setLocalMedicines(mergedList);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Handle Supplier Selection
  const handleSupplierChange = (e) => {
    const val = e.target.value;
    setSupplierInputText(val);
    setShowSupplierSuggestions(true);
    const found = suppliers.find(s => s.name.toUpperCase() === val.toUpperCase());
    setSelectedSupplier(found || { name: val, address: '', gst: '', phone: '' });
  };

    // Global ESC shortcut to dashboard
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          // Check if any modal is open before navigating
          if (!document.querySelector('.fixed.inset-0')) {
            navigate('/dashboard');
          }
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);

    const getInitialRow = (id) => ({
    id, packing: '', mfg: '', productName: '', qty: 1, freeQty: 0, sch: '',
    batch: '', exp: '', mrp: 0, rate: 0, disc: 0, gst: 0, costRate: 0, costPrice: 0, amount: 0 
  });

  const [rows, setRows] = useState([getInitialRow(Date.now())]);

  const getAvailableMedicines = (currentRowId) => {
    return localMedicines;
  };

  // Bottom section states
  const [cashDiscPercent, setCashDiscPercent] = useState('0');
  const [cashDisc, setCashDisc] = useState(0);
  const [isManualCashDisc, setIsManualCashDisc] = useState(false);
  const [lastModifiedCashDiscField, setLastModifiedCashDiscField] = useState('percent');
  const [itemDisc, setItemDisc] = useState(0);
  const [schDisc, setSchDisc] = useState(0);
  const [others, setOthers] = useState(0);
  const [lessAmount, setLessAmount] = useState(0);
  const [lessMode, setLessMode] = useState('PRE_TAX'); // 'PRE_TAX' (GST आधी) | 'POST_TAX' (GST नंतर) | 'BOTH'
  const [postTaxLess, setPostTaxLess] = useState(0);
  const [addAmountLocal, setAddAmountLocal] = useState(0);
  const [manualToPay, setManualToPay] = useState('');
  const [isManualToPay, setIsManualToPay] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Draft Auto-Save & Resume States for Purchase Bill
  const [hasRestoredPurchaseDraft, setHasRestoredPurchaseDraft] = useState(false);
  const [purchaseDraftSavedAt, setPurchaseDraftSavedAt] = useState(null);

  // 1. Restore purchase draft on mount (only if not editing an existing bill from location.state)
  useEffect(() => {
    try {
      if (location.state?.editBillId || location.state?.editBill) {
        setHasRestoredPurchaseDraft(true);
        return;
      }
      const savedDraft = localStorage.getItem('medical_purchase_draft');
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        if (draft && draft.rows && draft.rows.length > 0) {
          if (draft.date) setDate(draft.date);
          if (draft.paymentType) setPaymentType(draft.paymentType);
          if (draft.invoiceNo !== undefined) setInvoiceNo(draft.invoiceNo);
          if (draft.supplierInputText !== undefined) setSupplierInputText(draft.supplierInputText);
          if (draft.selectedSupplier) setSelectedSupplier(draft.selectedSupplier);
          if (draft.rows && draft.rows.length > 0) setRows(draft.rows);
          if (draft.cashDiscPercent !== undefined) setCashDiscPercent(draft.cashDiscPercent);
          if (draft.cashDisc !== undefined) { setCashDisc(draft.cashDisc); setIsManualCashDisc(true); }
          if (draft.itemDisc !== undefined) setItemDisc(draft.itemDisc);
          if (draft.schDisc !== undefined) setSchDisc(draft.schDisc);
          if (draft.others !== undefined) setOthers(draft.others);
          if (draft.lessAmount !== undefined) setLessAmount(draft.lessAmount);
          if (draft.lessMode) setLessMode(draft.lessMode);
          if (draft.postTaxLess !== undefined) setPostTaxLess(draft.postTaxLess);
          if (draft.addAmountLocal !== undefined) setAddAmountLocal(draft.addAmountLocal);
          if (draft.manualToPay !== undefined) { setManualToPay(draft.manualToPay); setIsManualToPay(true); }
          if (draft.uploadedDoc) { setUploadedDoc(draft.uploadedDoc); setIsSplitView(true); }
          if (draft.extractedData) setExtractedData(draft.extractedData);
          setPurchaseDraftSavedAt(draft.savedAt || 'आधीचा ड्राफ्ट');
        }
      }
    } catch (e) {
      console.warn('Could not restore purchase draft:', e);
    } finally {
      setHasRestoredPurchaseDraft(true);
    }
  }, [location.state]);

  // 2. Auto-save purchase draft to localStorage as user types
  useEffect(() => {
    if (!hasRestoredPurchaseDraft || editingBillId) return;

    const hasContent = (rows && rows.some(r => (r.productName && r.productName.trim() !== '') || (parseFloat(r.amount) || 0) > 0)) ||
                       (supplierInputText && supplierInputText.trim() !== '') ||
                       (invoiceNo && invoiceNo.trim() !== '');

    if (hasContent) {
      const draftData = {
        date,
        paymentType,
        invoiceNo,
        supplierInputText,
        selectedSupplier,
        rows,
        cashDiscPercent, cashDisc, isManualCashDisc, itemDisc, schDisc, others,
        lessAmount, lessMode, postTaxLess, addAmountLocal, manualToPay, isManualToPay,
        uploadedDoc, extractedData,
        savedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        timestamp: Date.now()
      };
      localStorage.setItem('medical_purchase_draft', JSON.stringify(draftData));
      setPurchaseDraftSavedAt(draftData.savedAt);
    } else {
      localStorage.removeItem('medical_purchase_draft');
      setPurchaseDraftSavedAt(null);
    }
  }, [
    hasRestoredPurchaseDraft, rows, supplierInputText, selectedSupplier, invoiceNo,
    date, paymentType, cashDiscPercent, cashDisc, isManualCashDisc, itemDisc,
    schDisc, others, lessAmount, lessMode, postTaxLess, addAmountLocal, manualToPay,
    isManualToPay, uploadedDoc, extractedData, editingBillId
  ]);

  // 3. Clear purchase draft & reset form
  const handleClearPurchaseDraft = () => {
    if (window.confirm("चालू खरेदी ड्राफ्ट बिल क्लिअर करून नवीन रिकामे बिल सुरू करायचे आहे का? (Clear this draft and start a new blank purchase bill?)")) {
      localStorage.removeItem('medical_purchase_draft');
      setPurchaseDraftSavedAt(null);
      setEditingBillId(null);
      setRows([getInitialRow(Date.now())]);
      setSelectedSupplier(null);
      setSupplierInputText('');
      setInvoiceNo('');
      setDate(new Date().toISOString().split('T')[0]);
      setCashDiscPercent('0'); setCashDisc(0); setIsManualCashDisc(false); setItemDisc(0); setSchDisc(0); setOthers(0);
      setLessAmount(0); setAddAmountLocal(0); setManualToPay(''); setIsManualToPay(false);
      setUploadedDoc(null);
      setCurrentFile(null);
      setExtractedData(null);
      setIsSplitView(false);
    }
  };

  // 4. Warn before closing if items entered
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasContent = rows && rows.some(r => r.productName && r.productName.trim() !== '');
      if (hasContent && !editingBillId) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [rows, editingBillId]);

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(r => r.id !== id));
    }
  };

  const updateRow = (id, field, value) => {
    setRows(prevRows => {
      let isLastRowAndRequiresNew = false;
      const newRows = prevRows.map((r, index) => {
        if (r.id === id) {
          const updated = { ...r, [field]: value };
          if (field === 'sch') {
            const parsed = parseFreeUnits(value);
            updated.freeQty = parsed;
            updated.sch = value !== undefined && value !== null ? String(value) : '';
          } else if (field === 'freeQty') {
            const parsed = parseFreeUnits(value);
            updated.freeQty = parsed;
            updated.sch = value ? String(value) : '';
          }

          // Recalculate amount when relevant fields change
          if (['qty', 'rate', 'mrp', 'disc', 'gst', 'freeQty', 'sch', 'scmDiscAmt'].includes(field)) {
            let mrp = parseFloat(updated.mrp) || 0;
            let rate = parseFloat(updated.rate) || 0;

            // If MRP is provided and Rate is 0/empty, auto-default Rate to 80% of MRP
            if (mrp > 0 && rate === 0 && field !== 'rate') {
              rate = Math.round(mrp * 0.8 * 100) / 100;
              updated.rate = rate;
            }

            const qty = parseFloat(updated.qty) || 0;
            const discVal = parseFloat(updated.disc) || 0;
            const scmDiscAmt = parseFloat(updated.scmDiscAmt) || 0;
            const baseAmount = qty * rate;
            let discAmt = 0;
            if (discVal > 0) {
              if (typeof updated.disc === 'string' && updated.disc.includes('%')) {
                discAmt = (baseAmount * parseFloat(updated.disc)) / 100;
              } else {
                discAmt = Math.min(baseAmount, discVal);
              }
            }
            updated.amount = Math.max(0, Math.round((baseAmount - discAmt - scmDiscAmt) * 100) / 100);
            const costVal = computeRowCostRate(updated);
            updated.costRate = costVal;
            updated.costPrice = costVal;
          }
          
          // If we are modifying the very last row and the value is not empty, spawn a new empty row
          if (index === prevRows.length - 1 && value.toString().trim() !== '' && field !== 'showSuggestions') {
            isLastRowAndRequiresNew = true;
          }
          
          return updated;
        }
        return r;
      });

      if (isLastRowAndRequiresNew) {
        newRows.push(getInitialRow(Date.now()));
      }

      return newRows;
    });
  };

  const handleSaveSupplier = async () => {
    if (!newSupplier.name.trim()) {
      alert("Supplier name is required.");
      return;
    }
    try {
      const response = await fetch(apiUrl('/api/suppliers'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSupplier)
      });
      if (response.ok) {
        const saved = await response.json();
        setSuppliers([...suppliers, saved]);
        setShowSupplierModal(false);
        setNewSupplier({ name: '', address: '', phone: '', gst: '' });
        
        // Auto-select the newly added supplier
        setSelectedSupplier(saved);
        setSupplierInputText(saved.name);
      }
    } catch (error) {
      console.error("Failed to save supplier", error);
      alert("Failed to save supplier.");
    }
  };

  // Calculations
  const validRows = rows.filter(r => r.productName.trim() !== '');

  // Gross Amount: Total product value before discounts (Qty * Rate)
  const grossAmount = validRows.reduce((sum, r) => {
    const q = parseFloat(r.qty) || 0;
    const rt = parseFloat(r.rate) || 0;
    return sum + (q * rt > 0 ? q * rt : (parseFloat(r.amount) || 0));
  }, 0);

  // Row amounts sum (Total after item-level discounts)
  const rowTaxableSum = validRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalRowDiscs = Math.max(0, grossAmount - rowTaxableSum);

  // Base amount for bill-level discounts (Cash Disc % applies on subtotal after item-level discounts)
  const baseForBillDiscount = rowTaxableSum > 0 ? rowTaxableSum : grossAmount;

  // Bill-level discounts & deductions
  const cashDiscVal = parseFloat(cashDisc || 0);
  const itemDiscVal = parseFloat(itemDisc || 0);
  const schDiscVal = parseFloat(schDisc || 0);
  const othersVal = parseFloat(others || 0);
  const lessAmountVal = parseFloat(lessAmount || 0);
  const postTaxLessVal = parseFloat(postTaxLess || 0);

  // पद्धत १ (Pre-Tax Less / GST आधी): Gross मधून वजा होऊन Taxable Base ठरते
  // पद्धत २ (Post-Tax Less / GST नंतर): GST लागल्यानंतर Final Net बिलातून थेट वजा होते
  let preTaxLess = 0;
  let finalPostTaxLess = 0;

  if (lessMode === 'PRE_TAX') {
    preTaxLess = lessAmountVal;
    finalPostTaxLess = 0;
  } else if (lessMode === 'POST_TAX') {
    preTaxLess = 0;
    finalPostTaxLess = lessAmountVal;
  } else if (lessMode === 'BOTH') {
    preTaxLess = lessAmountVal;
    finalPostTaxLess = postTaxLessVal;
  }

  // Pre-tax deduction calculation:
  // Pre-tax discount determines Taxable Base (Base - PreTaxDiscounts).
  // If lessMode is PRE_TAX: Cash Disc and Less Amount refer to the same pre-tax deduction; do NOT add both!
  // If lessMode is POST_TAX: Less Amount is deducted after GST, so do NOT also deduct it pre-tax!
  const breakdownDiscounts = cashDiscVal + schDiscVal + itemDiscVal + othersVal;
  let effectivePreTaxDiscounts = 0;
  if (lessMode === 'POST_TAX') {
    effectivePreTaxDiscounts = breakdownDiscounts;
  } else {
    // PRE_TAX or BOTH: Less Amount is the total pre-tax deduction on bill (which matches or exceeds the breakdown)
    effectivePreTaxDiscounts = Math.max(lessAmountVal, breakdownDiscounts);
  }

  // Taxable Base:
  // Taxable Base = Base - Less Amount (Exact Match with printed bill!)
  let taxableAmount = Math.max(0, baseForBillDiscount - effectivePreTaxDiscounts);

  // GST Tax Calculation:
  // In Indian pharmaceutical accounting, pre-tax discounts (Less) proportionally reduce the taxable base before GST
  const discountRatio = (baseForBillDiscount > 0 && effectivePreTaxDiscounts > 0)
    ? Math.max(0, 1 - (effectivePreTaxDiscounts / baseForBillDiscount))
    : 1;

  const computedTaxFromRows = validRows.reduce((sum, r) => {
    const gstPercent = parseFloat(r.gst) || 0;
    const base = (parseFloat(r.amount) || 0) * discountRatio;
    return sum + (base * (gstPercent / 100));
  }, 0);

  // Authoritative bill tax: if extractedData has printed tax, prioritize it when isManualToPay is active
  const printedTax = extractedData && (parseFloat(extractedData.totalTax) > 0 ? parseFloat(extractedData.totalTax) : (parseFloat(extractedData.cgstAmount || 0) + parseFloat(extractedData.sgstAmount || 0)));
  const totalTaxAmount = (printedTax > 0 && isManualToPay)
    ? printedTax
    : Math.round(computedTaxFromRows * 100) / 100;

  // Auto-calculate Cash Discount % when baseForBillDiscount or cashDiscPercent changes
  useEffect(() => {
    const base = baseForBillDiscount;
    if (base <= 0) return;
    if (lastModifiedCashDiscField === 'amount') {
      const numVal = parseFloat(cashDisc) || 0;
      if (numVal > 0) {
        const pct = Math.round((numVal / base) * 100 * 10) / 10;
        setCashDiscPercent(String(pct));
      }
    } else {
      const percentVal = parseFloat(cashDiscPercent) || 0;
      if (percentVal > 0) {
        const computed = (base * percentVal) / 100;
        setCashDisc(computed.toFixed(2));
      }
    }
  }, [baseForBillDiscount, cashDiscPercent, lastModifiedCashDiscField]);

  const totalQty = validRows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0);
  const totalItems = validRows.length;

  // Computed net amount before rounding (subtracting Post-Tax Less if Method 2 applies!)
  const calculatedNet = Math.max(0, taxableAmount + totalTaxAmount + parseFloat(addAmountLocal || 0) - finalPostTaxLess);
  const autoToPay = Math.round(calculatedNet);

  // Final To Pay: manualToPay if set by user or image OCR, else autoToPay
  const toPay = (isManualToPay && manualToPay !== '' && !isNaN(parseFloat(manualToPay)))
    ? parseFloat(manualToPay)
    : autoToPay;

  const roundOff = (toPay - calculatedNet).toFixed(2);

  const handleSaveBill = async () => {
    if (isSaving) return; // Prevent double submit
    if (validRows.length === 0) {
      alert("Please add at least one product with an amount before saving.");
      return;
    }
    if (!selectedSupplier || !selectedSupplier.name) {
      alert("Please select or enter a supplier.");
      return;
    }

    // Check client-side for duplicate invoice entry
    if (invoiceNo && invoiceNo.trim() && !editingBillId) {
      const checkInv = invoiceNo.trim().toUpperCase();
      const checkSupp = selectedSupplier.name.trim().toUpperCase();
      const isDuplicate = allPurchases.some(p => 
        p.invoiceNo && p.invoiceNo.trim().toUpperCase() === checkInv &&
        p.supplierName && p.supplierName.trim().toUpperCase() === checkSupp
      );
      if (isDuplicate) {
        alert(`⚠️ Invoice #${invoiceNo.trim()} for "${selectedSupplier.name}" is already saved in the system! Duplicate entry is not allowed.`);
        return;
      }
    }

    try {
      setIsSaving(true);
      const purchaseData = {
        vchNo,
        date,
        supplierName: selectedSupplier.name,
        supplierAddress: selectedSupplier.address || '',
        supplierPhone: selectedSupplier.phone || '',
        supplierGst: selectedSupplier.gst || '',
        buyerName: 'Sai Pharmacy',
        pharmacyName: 'Sai Pharmacy',
        paymentType,
        invoiceNo,
        taxableAmount,
        totalTaxAmount,
        grossAmount,
        cashDiscPercent: parseFloat(cashDiscPercent) || 0,
        cashDisc: parseFloat(cashDisc) || 0,
        itemDisc: parseFloat(itemDisc) || 0,
        schDisc: parseFloat(schDisc) || 0,
        others: parseFloat(others) || 0,
        lessAmount: parseFloat(lessAmount) || 0,
        lessMode: lessMode || 'PRE_TAX',
        postTaxLess: parseFloat(postTaxLess) || 0,
        preTaxLess: preTaxLess,
        roundOff: parseFloat(roundOff) || 0,
        addAmount: parseFloat(addAmountLocal) || 0,
        totalItems: totalItems,
        totalQty: totalQty,
        netAmount: parseFloat(toPay),
        billImage: uploadedDoc || null,
        extractedData: extractedData || null,
        items: validRows.map(r => {
          const rowGst = parseFloat(r.gst);
          const finalGst = (!isNaN(rowGst) && rowGst >= 0) ? rowGst : 0;
          const costVal = computeRowCostRate({ ...r, gst: finalGst });
          const freeVal = parseFreeUnits(r.freeQty, r.sch);
          return {
            ...r,
            qty: parseFloat(r.qty) || 0,
            rate: parseFloat(r.rate) || 0,
            costRate: costVal,
            costPrice: costVal,
            mrp: parseFloat(r.mrp) || 0,
            disc: parseFloat(r.disc) || 0,
            sch: r.sch || (freeVal > 0 ? String(freeVal) : ''),
            freeQty: freeVal,
            gst: finalGst,
            amount: parseFloat(r.amount) || 0
          };
        })
      };

      const method = editingBillId ? 'PUT' : 'POST';
      const url = editingBillId 
        ? apiUrl(`/api/purchases/${editingBillId}`)
        : apiUrl('/api/purchases');

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.error || 'Failed to save on backend');
      }
      
      const savedData = await response.json();
      
      // Auto-register any new products into customMedicines and localMedicines dropdown
      const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
      let updatedCustom = false;
      validRows.forEach(r => {
        if (r.productName) {
          const pUpper = r.productName.toUpperCase().trim();
          if (!custom.some(m => m.name.toUpperCase() === pUpper) && !vetMedicines.some(m => m.name.toUpperCase() === pUpper)) {
            custom.push({ name: pUpper, company: r.mfg || '' });
            updatedCustom = true;
          }
        }
      });
      if (updatedCustom) {
        localStorage.setItem('customMedicines', JSON.stringify(custom));
        setLocalMedicines(prev => {
          const combinedMap = new Map();
          [...prev, ...custom].forEach(m => {
            if (m && m.name) {
              const key = m.name.toUpperCase().trim();
              if (!combinedMap.has(key)) combinedMap.set(key, { name: key, company: m.company || m.mfg || '' });
            }
          });
          return Array.from(combinedMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        });
      }

      if (editingBillId) {
        setAllPurchases(allPurchases.map(p => p.id === editingBillId ? savedData : p));
        alert('Bill Updated Successfully!');
      } else {
        setAllPurchases([savedData, ...allPurchases]);
        alert('Bill Saved Successfully!');
      }
      
      // Clear draft on successful save
      localStorage.removeItem('medical_purchase_draft');
      setPurchaseDraftSavedAt(null);

      // Reset form and reset single-upload image state
      setRows([getInitialRow(Date.now())]);
      setSelectedSupplier(null);
      setSupplierInputText('');
      setInvoiceNo('');
      setVchNo(editingBillId ? vchNo : allPurchases.length + 2);
      setCashDiscPercent('0'); setCashDisc(0); setIsManualCashDisc(false); setItemDisc(0); setSchDisc(0); setOthers(0);
      setLessAmount(0); setAddAmountLocal(0); setManualToPay(''); setIsManualToPay(false);
      setEditingBillId(null);
      setUploadedDoc(null);
      setCurrentFile(null);
      setExtractedData(null);
    } catch (error) {
      console.error("Error saving bill: ", error);
      alert(error.message || 'Failed to save bill. Make sure your backend server is running.');
    } finally {
      setIsSaving(false);
    }
  };

  const loadBillForEditing = (bill) => {
    setEditingBillId(bill.id);
    setDate(bill.date || new Date().toISOString().split('T')[0]);
    setInvoiceNo(bill.invoiceNo || '');
    setVchNo(bill.vchNo || '');
    setPaymentType(bill.paymentType || 'Credit');
    
    // Load attached bill photo and extraction data if present
    if (bill.billImage) {
      setUploadedDoc(bill.billImage);
      setIsSplitView(true);
    } else {
      setUploadedDoc(null);
    }
    if (bill.extractedData) {
      setExtractedData(bill.extractedData);
    } else {
      setExtractedData(null);
    }
    
    // Attempt to select the supplier
    const supplierName = bill.supplierName || '';
    setSupplierInputText(supplierName);
    const foundSupplier = suppliers.find(s => s.name.toUpperCase() === supplierName.toUpperCase());
    setSelectedSupplier(foundSupplier || { name: supplierName, address: '', gst: '', phone: '' });

    // Load items
    if (bill.items && bill.items.length > 0) {
       // Ensure there's always an empty row at the end for easy addition
       setRows([...bill.items.map((item, idx) => ({ ...item, id: Date.now() + idx })), getInitialRow(Date.now() + 1000)]);
    } else {
       setRows([getInitialRow(Date.now())]);
    }

    const billGross = parseFloat(bill.grossAmount || bill.taxableAmount || 0);
    const billCashDisc = parseFloat(bill.cashDisc || 0);
    const billLess = parseFloat(bill.lessAmount || 0);
    const billItemDisc = parseFloat(bill.itemDisc || 0);
    const billSchDisc = parseFloat(bill.schDisc || 0);
    const totalD = billLess > 0 ? billLess : (billCashDisc + billItemDisc + billSchDisc);
    
    let discPct = parseFloat(bill.cashDiscPercent || 0);
    // Auto-calculate discount % if it was saved as 0 or missing but discount exists!
    if (discPct === 0 && billGross > 0) {
      if (billCashDisc > 0) {
        discPct = Math.round((billCashDisc / billGross) * 100 * 10) / 10;
      } else if (totalD > 0) {
        discPct = Math.round((totalD / billGross) * 100 * 10) / 10;
      }
    }

    setCashDiscPercent(String(discPct));
    if (bill.cashDisc !== undefined) { 
      setCashDisc(bill.cashDisc); 
      setIsManualCashDisc(true); 
    }
    setItemDisc(bill.itemDisc || 0); 
    setSchDisc(bill.schDisc || 0);
    setOthers(bill.others || 0);
    setLessAmount(bill.lessAmount || 0);
    setLessMode(bill.lessMode || (bill.postTaxLess > 0 ? 'POST_TAX' : 'PRE_TAX'));
    setPostTaxLess(bill.postTaxLess || 0); 
    setAddAmountLocal(bill.addAmountLocal || bill.addAmount || 0);
    if (bill.netAmount !== undefined && bill.netAmount !== null) {
      setManualToPay(String(bill.netAmount));
      setIsManualToPay(true);
    } else {
      setManualToPay('');
      setIsManualToPay(false);
    }

    setShowPastBillsDrawer(false);
  };

  const cancelEditing = () => {
    setEditingBillId(null);
    setRows([getInitialRow(Date.now())]);
    setSelectedSupplier(null);
    setSupplierInputText('');
    setInvoiceNo('');
    setVchNo(allPurchases.length + 1);
    setDate(new Date().toISOString().split('T')[0]);
    setUploadedDoc(null);
    setCurrentFile(null);
    setExtractedData(null);
    setIsSplitView(false);
  };

  const handleDeletePurchaseBill = async (billId) => {
    if (!billId) return;
    if (!window.confirm("Are you sure you want to delete this purchase bill? This will remove all its items from the system.")) return;
    try {
      const res = await fetch(apiUrl(`/api/purchases/${billId}`), { method: 'DELETE' });
      if (res.ok) {
        setAllPurchases(prev => prev.filter(p => p.id !== billId));
        if (editingBillId === billId) {
          cancelEditing();
        }
        alert("Purchase bill deleted successfully!");
      } else {
        alert("Failed to delete purchase bill on server.");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting bill: " + err.message);
    }
  };

  return (
    <div className="animate-in fade-in duration-700 flex flex-col h-[calc(100vh-4.2rem)] max-h-[calc(100vh-4.2rem)] overflow-hidden pb-1">
        {/* Page Title & Top Action Controls */}
        <div className="flex items-center justify-between mb-1 shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg md:text-xl font-bold text-slate-800 tracking-wide">
              {editingBillId ? 'Edit Purchase Invoice' : 'Purchases Entry'}
            </h2>
            {editingBillId && (
              <div className="flex items-center gap-1.5 ml-2">
                <span className="px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded text-xs font-black flex items-center gap-1">
                  ✏️ EDITING BILL #{invoiceNo || editingBillId}
                </span>
                <button 
                  onClick={() => {
                    setShowPastBillsDrawer(true);
                    setInvoiceSearchTerm('');
                    setSelectedBillIndex(0);
                    setTimeout(() => invoiceSearchInputRef.current?.focus(), 60);
                  }} 
                  className="px-2 py-0.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-black shadow-xs transition-colors flex items-center gap-1"
                  title="Switch to edit another bill (F3)"
                >
                  <span className="bg-amber-700/60 px-1 py-0.2 rounded text-[10px]">F3</span>
                  Switch Bill
                </button>
                <button onClick={cancelEditing} className="px-2.5 py-0.5 bg-slate-200 text-slate-700 rounded text-xs font-bold hover:bg-slate-300 transition-colors">
                  Cancel (Esc)
                </button>
                <button onClick={() => handleDeletePurchaseBill(editingBillId)} className="px-2.5 py-0.5 bg-rose-600 text-white rounded text-xs font-bold hover:bg-rose-700 transition-colors flex items-center gap-1">
                  Delete
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-1.5 sm:gap-2 items-center">
            {purchaseDraftSavedAt && !editingBillId && (
              <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-800 px-2.5 py-1 rounded-lg text-xs font-bold shadow-xs animate-in fade-in duration-300">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>💾 ड्राफ्ट सेव्ह आहे ({purchaseDraftSavedAt})</span>
                <button 
                  type="button"
                  onClick={handleClearPurchaseDraft}
                  className="ml-1 px-1.5 py-0.2 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-300 rounded text-[10px] font-black transition-colors"
                  title="चालू ड्राफ्ट काढून नवीन कोरे खरेदी बिल सुरू करा"
                >
                  नवे बिल (Clear)
                </button>
              </div>
            )}
            <label className={`flex items-center gap-2 px-3 py-1 font-bold text-xs rounded-lg shadow-md transition-all ${
              isExtracting 
                ? 'bg-slate-400 text-white cursor-not-allowed pointer-events-none opacity-75' 
                : uploadedDoc 
                  ? 'bg-teal-800 text-teal-100 hover:bg-teal-900 border border-teal-500 cursor-pointer' 
                  : 'bg-gradient-to-r from-[#1e5c5c] to-teal-700 text-white hover:from-teal-800 hover:to-[#1e5c5c] cursor-pointer'
            }`}>
              <span>{isExtracting ? '⏳ Reading Bill...' : uploadedDoc ? '✅ Bill Attached (1 Image)' : '🤖 Auto Scan Bill (AI)'}</span>
              <input 
                type="file" 
                accept="image/jpeg,image/png,image/jpg,image/webp" 
                disabled={isExtracting}
                className="hidden" 
                onClick={(e) => {
                  if (isExtracting) {
                    e.preventDefault();
                    return;
                  }
                  if (uploadedDoc) {
                    const replaceConfirm = window.confirm('A bill image is already attached to this entry. Do you want to replace it with a new image?');
                    if (!replaceConfirm) {
                      e.preventDefault();
                    }
                  }
                }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    if (isExtracting) return;
                    const selectedFile = e.target.files[0];
                    e.target.value = ''; // Clean up input value so it cannot be double-submitted
                    handleExtractInvoice(selectedFile);
                  }
                }} 
              />
            </label>

            <button 
              onClick={() => setShowApiKeyModal(true)}
              className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100 rounded-lg font-bold text-xs shadow-sm transition-all"
              title="Configure Gemini AI Key for client"
            >
              🔑 AI Key
            </button>

            <button 
              onClick={() => {
                if (!uploadedDoc && !isSplitView) {
                  alert('Upload a bill photo first using "🤖 Auto Scan Bill (AI)" or select a past bill to use Side-by-Side Split View.');
                  return;
                }
                setIsSplitView(!isSplitView);
              }} 
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg font-bold text-xs shadow-sm transition-all border ${
                isSplitView 
                  ? 'bg-blue-600 text-white border-blue-700 ring-2 ring-blue-300' 
                  : uploadedDoc 
                    ? 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              title="Toggle Side-by-Side View of Bill Image & Purchase Entry"
            >
              <span>🖼️ {isSplitView ? 'Close Split View' : 'Split Bill View'}</span>
              {uploadedDoc && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>}
            </button>

            <button 
              onClick={() => {
                if (uploadedDoc) {
                  setShowImageModal(true);
                } else {
                  alert('No bill photo uploaded for this entry yet. Please click "🤖 Auto Scan Bill (AI)" to select a bill photo.');
                }
              }} 
              className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-300 text-teal-800 hover:bg-teal-100 rounded-lg font-bold text-xs shadow-sm transition-all"
              title="View uploaded bill photo & exact extracted values"
            >
              👁️ View Bill Photo
            </button>

            <button 
              onClick={() => {
                setShowPastBillsDrawer(true);
                setInvoiceSearchTerm('');
                setSelectedBillIndex(0);
                setTimeout(() => invoiceSearchInputRef.current?.focus(), 60);
              }}
              title="Press F3 to edit a purchase bill"
              className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-400 text-amber-900 hover:bg-amber-100 hover:border-amber-500 rounded-lg font-black text-xs shadow-sm transition-all cursor-pointer"
            >
              <span className="bg-amber-500 text-white px-1.5 py-0.2 rounded text-[10px] font-black shadow-xs">F3</span>
              <span>✏️ Edit Bill</span>
            </button>
            {/* Add Product Button (Icon with F2 badge - no text name) */}
            <button 
              type="button"
              onClick={() => {
                setActiveProductRowId(selectedRowId || null);
                setShowProductModal(true);
              }}
              title="Add New Product (F2 / Insert) - नवीन प्रॉडक्ट ऍड करा"
              className="flex items-center gap-1 px-2.5 py-1 bg-teal-50 border border-teal-400 text-teal-900 hover:bg-teal-100 hover:border-teal-500 rounded-lg font-black text-xs shadow-sm transition-all cursor-pointer"
            >
              <span className="bg-teal-600 text-white px-1.5 py-0.2 rounded text-[10px] font-black shadow-xs">F2</span>
              <svg className="w-3.5 h-3.5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Edit Product Button (Icon with F4 badge) */}
            <button 
              type="button"
              onClick={() => {
                const activeRow = rows.find(r => r.id === selectedRowId && r.productName?.trim());
                openEditProductModal(activeRow ? activeRow.productName : null);
              }}
              title="Edit Product (F4) - प्रॉडक्ट माहिती एडिट करा"
              className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 border border-indigo-400 text-indigo-900 hover:bg-indigo-100 hover:border-indigo-500 rounded-lg font-black text-xs shadow-sm transition-all cursor-pointer"
            >
              <span className="bg-indigo-600 text-white px-1.5 py-0.2 rounded text-[10px] font-black shadow-xs">F4</span>
              <svg className="w-3.5 h-3.5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>

      {/* Attached Bill Photo Notification Strip */}
      {uploadedDoc && (
        <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-teal-950 text-white px-2.5 py-1 rounded-lg flex items-center justify-between shadow-xs mb-1 text-[11px] font-bold border border-teal-600/40 shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1 text-teal-300 font-black">
              <span>📷 Bill Attached</span>
              {invoiceNo && <span className="bg-teal-800/80 text-teal-200 px-1.5 py-0.5 rounded text-[10px]">#{invoiceNo}</span>}
            </span>
            <span className="text-white/30 hidden sm:inline">|</span>
            <span className="text-slate-300">Gross: <strong className="text-white">₹{grossAmount.toFixed(2)}</strong></span>
            <span className="text-white/30 hidden sm:inline">|</span>
            {effectivePreTaxDiscounts > 0 && (
              <>
                <span className="text-rose-300">Disc: <strong className="text-rose-200">-₹{effectivePreTaxDiscounts.toFixed(2)}</strong></span>
                <span className="text-white/30 hidden sm:inline">|</span>
              </>
            )}
            {finalPostTaxLess > 0 && (
              <>
                <span className="text-amber-300">Less: <strong className="text-amber-200">-₹{finalPostTaxLess.toFixed(2)}</strong></span>
                <span className="text-white/30 hidden sm:inline">|</span>
              </>
            )}
            <span className="text-emerald-300">GST: <strong className="text-emerald-200">+₹{totalTaxAmount.toFixed(2)}</strong></span>
            <span className="text-white/30 hidden sm:inline">|</span>
            <span className="text-teal-200">Net To Pay: <strong className="text-white font-black text-xs">₹{toPay.toFixed(2)}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button 
              type="button"
              onClick={() => setIsSplitView(!isSplitView)}
              className="px-2.5 py-0.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-[11px] font-black transition-all shadow-xs flex items-center gap-1"
              title={isSplitView ? 'Click to show product table full width' : 'Click to show side-by-side split view'}
            >
              {isSplitView ? '↔ Full Table View (विस्तृत टेबल)' : '🖼️ Side-by-Side Split'}
            </button>
            <button 
              type="button"
              onClick={() => setShowImageModal(true)}
              className="px-2 py-0.5 bg-teal-700 hover:bg-teal-600 text-white rounded text-[11px] font-bold transition-colors"
            >
              👁️ Fullscreen
            </button>
            <button 
              type="button"
              onClick={() => {
                if (window.confirm("Remove attached bill photo from this entry?")) {
                  setUploadedDoc(null);
                  setCurrentFile(null);
                  setExtractedData(null);
                  setIsSplitView(false);
                }
              }}
              className="px-2 py-0.5 bg-rose-600/80 hover:bg-rose-600 text-white rounded text-[11px] font-bold transition-colors"
              title="Detach photo"
            >
              ✕ Detach
            </button>
          </div>
        </div>
      )}

      {/* Main Body: Split View (Image Left + Entry Right) OR Full Width */}
      <div className={`flex-1 flex gap-2 overflow-hidden min-h-0 ${isSplitView && uploadedDoc ? 'flex-row' : 'flex-col'}`}>
        {/* Left Side: Sticky Bill Photo Inspector in Split View */}
        {isSplitView && uploadedDoc && (
          <div 
            style={{ width: splitWidth }}
            className="flex flex-col bg-slate-900 border-2 border-teal-600/60 rounded-xl overflow-hidden shadow-xl shrink-0 h-full min-h-0 animate-in slide-in-from-left duration-200"
          >
            {/* Header controls for Image Viewer */}
            <div className="p-1 px-2 bg-gradient-to-r from-teal-950 to-slate-900 text-white flex items-center justify-between border-b border-teal-700/50 shrink-0">
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-xs">📷</span>
                <span className="text-[10px] font-black uppercase tracking-wider text-teal-300 truncate">Photo</span>
              </div>

              {/* Split Width Presets */}
              <div className="flex items-center gap-0.5 bg-slate-800/90 p-0.5 rounded border border-teal-700/40">
                <button
                  type="button"
                  onClick={() => setSplitWidth('25%')}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-black transition-all ${splitWidth === '25%' ? 'bg-teal-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
                  title="Compact Photo (25% photo / 75% product table)"
                >
                  25%
                </button>
                <button
                  type="button"
                  onClick={() => setSplitWidth('30%')}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-black transition-all ${splitWidth === '30%' ? 'bg-teal-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
                  title="Balanced (30% photo / 70% product table)"
                >
                  30%
                </button>
                <button
                  type="button"
                  onClick={() => setSplitWidth('38%')}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-black transition-all ${splitWidth === '38%' ? 'bg-teal-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
                  title="Wide Photo (38% photo / 62% product table)"
                >
                  38%
                </button>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setImageZoom(prev => Math.min(prev + 0.25, 3))}
                  className="px-1.5 py-0.5 bg-slate-800 hover:bg-slate-700 text-teal-200 text-xs font-black rounded border border-teal-700/50"
                  title="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => setImageZoom(prev => Math.max(prev - 0.25, 0.5))}
                  className="px-1.5 py-0.5 bg-slate-800 hover:bg-slate-700 text-teal-200 text-xs font-black rounded border border-teal-700/50"
                  title="Zoom Out"
                >
                  -
                </button>
                <button
                  type="button"
                  onClick={() => setImageZoom(1)}
                  className="px-1 py-0.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-bold rounded"
                  title="Reset Zoom"
                >
                  1:1
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = (currentRotation + 90) % 360;
                    setCurrentRotation(next);
                    if (currentFile) handleExtractInvoice(currentFile, next);
                  }}
                  className="px-1 py-0.5 bg-teal-800 hover:bg-teal-700 text-white text-[9px] font-bold rounded"
                  title="Rotate 90° CW"
                >
                  🔄
                </button>
                <button
                  type="button"
                  onClick={() => setShowImageModal(true)}
                  className="px-1.5 py-0.5 bg-blue-700 hover:bg-blue-600 text-white text-[9px] font-bold rounded ml-0.5"
                  title="Full Modal Preview"
                >
                  ⛶
                </button>
                <button
                  type="button"
                  onClick={() => setIsSplitView(false)}
                  className="px-1.5 py-0.5 bg-rose-800 hover:bg-rose-700 text-white text-[10px] font-bold rounded"
                  title="Close Side-by-Side (Give full width to table)"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Scrollable & Zoomable Image Box */}
            <div className="flex-1 overflow-auto p-2 bg-slate-950/90 relative min-h-0 flex items-start justify-center">
              <div style={{ width: `${Math.round(imageZoom * 100)}%`, minWidth: '100%', transition: 'width 0.15s ease' }}>
                <img 
                  src={uploadedDoc} 
                  alt="Bill Document"
                  className="w-full h-auto rounded shadow-md border border-slate-700 block"
                />
              </div>
            </div>

            {/* Collapsible Extracted Bill Values Card */}
            <div className="bg-slate-900 border-t border-teal-700/50 shrink-0 text-white text-xs">
              <button 
                type="button"
                onClick={() => setShowLeftCard(!showLeftCard)}
                className="w-full p-1.5 px-2 flex items-center justify-between hover:bg-slate-800/80 transition-colors text-left"
                title="Toggle Photo Breakdown Values"
              >
                <span className="text-[10px] font-black uppercase tracking-wider text-teal-300 flex items-center gap-1.5 truncate">
                  <span>📊 Photo:</span>
                  <span className="text-white font-black">Gross ₹{grossAmount.toFixed(0)}</span>
                  <span className="text-emerald-300 font-black">Net ₹{toPay.toFixed(0)}</span>
                </span>
                <span className="text-[9px] text-teal-300 font-mono shrink-0">
                  {showLeftCard ? '▲ Hide' : '▼ Details'}
                </span>
              </button>

              {showLeftCard && (
                <div className="p-2 pt-0 grid grid-cols-3 gap-1 text-[10px] animate-in fade-in duration-150">
                  <div className="bg-slate-800/90 p-1 rounded border border-slate-700">
                    <div className="text-[8px] text-slate-400 font-bold uppercase">Gross Total</div>
                    <div className="font-black text-white text-xs">₹{grossAmount.toFixed(2)}</div>
                  </div>
                  <div className="bg-rose-950/60 p-1 rounded border border-rose-800/40">
                    <div className="text-[8px] text-rose-300 font-bold uppercase">Discount</div>
                    <div className="font-black text-rose-400 text-xs">-₹{(preTaxLess > 0 ? preTaxLess : ((parseFloat(cashDisc) || 0) + (parseFloat(itemDisc) || 0) + (parseFloat(schDisc) || 0))).toFixed(2)}</div>
                  </div>
                  <div className="bg-amber-950/60 p-1 rounded border border-amber-800/40">
                    <div className="text-[8px] text-amber-300 font-bold uppercase">Less</div>
                    <div className="font-black text-amber-400 text-xs">-₹{(parseFloat(lessAmount) || 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-blue-950/60 p-1 rounded border border-blue-800/40">
                    <div className="text-[8px] text-blue-300 font-bold uppercase">Taxable</div>
                    <div className="font-black text-blue-300 text-xs">₹{taxableAmount.toFixed(2)}</div>
                  </div>
                  <div className="bg-emerald-950/60 p-1 rounded border border-emerald-800/40">
                    <div className="text-[8px] text-emerald-300 font-bold uppercase">GST</div>
                    <div className="font-black text-emerald-400 text-xs">+₹{totalTaxAmount.toFixed(2)}</div>
                  </div>
                  <div className="bg-teal-900/90 p-1 rounded border border-teal-500 shadow-sm text-center">
                    <div className="text-[8px] text-teal-200 font-bold uppercase">Net To Pay</div>
                    <div className="font-black text-xs text-white">₹{toPay.toFixed(2)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Side: Purchase Form (Takes full width if Split View is off) */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 h-full">
          {/* Invoice Header Section (Matches the Image Top Boxes) */}
          <div className="flex justify-between border-2 border-slate-300 bg-white rounded-lg overflow-visible shadow-sm text-xs font-bold text-slate-800 shrink-0 mb-1">
        
        {/* Left: Supplier Details */}
        <div className="flex-1 min-w-[240px] max-w-[360px] p-1 px-2 border-r-2 border-slate-300 relative flex flex-col justify-between">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-slate-500 font-bold text-[10px]">Inv. Date:</span>
            <input 
              type="date" 
              value={date} 
              onChange={e=>setDate(e.target.value)} 
              className="w-24 text-right outline-none bg-transparent font-black text-[#1e5c5c] cursor-pointer text-xs" 
            />
          </div>
          <div className="flex items-center justify-between mb-0.5">
            <input 
              type="text" 
              value={supplierInputText}
              onChange={handleSupplierChange}
              onFocus={() => setShowSupplierSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 200)}
              placeholder="Select or Type Supplier Name"
              className="w-full text-xs font-black text-[#1e5c5c] outline-none border-b border-slate-200 focus:border-[#1e5c5c] uppercase pb-0 bg-transparent"
            />
            {showSupplierSuggestions && supplierInputText.length > 0 && (
                <div className="absolute z-[100] left-0 top-full mt-1 w-[450px] bg-white border-2 border-[#1e5c5c]/30 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] max-h-[50vh] overflow-y-auto">
                  {suppliers.filter(s => s.name.toLowerCase().includes(supplierInputText.toLowerCase())).length > 0 ? (
                    suppliers
                      .filter(s => s.name.toLowerCase().includes(supplierInputText.toLowerCase()))
                      .map((s, idx) => (
                        <div 
                          key={idx}
                          className="px-4 py-2 hover:bg-[#1e5c5c]/10 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSupplierInputText(s.name);
                            setSelectedSupplier(s);
                            setShowSupplierSuggestions(false);
                          }}
                        >
                          <div className="text-[13px] font-bold text-slate-800">{s.name}</div>
                          <div className="text-[11px] font-bold text-slate-400 mt-1 flex gap-3">
                            {s.phone && <span>📞 {s.phone}</span>}
                            {s.gst && <span>📋 GST: {s.gst}</span>}
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="px-4 py-3 text-[11px] font-bold text-slate-400 text-center bg-slate-50 rounded-xl">Custom supplier.</div>
                  )}
                </div>
            )}
            <button 
              onClick={() => setShowSupplierModal(true)}
              className="ml-1 shrink-0 bg-teal-50 hover:bg-teal-100 text-teal-600 p-0.5 rounded border border-teal-200 shadow-xs transition-colors"
              title="Add New Supplier"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
            </button>
          </div>
          
          <div className="text-[10px] leading-tight text-slate-500 uppercase truncate">
            {selectedSupplier && selectedSupplier.id ? (
              <span className="truncate">
                {selectedSupplier.address ? <span>{selectedSupplier.address} • </span> : ''}
                GSTIN: <span className="text-slate-800 font-bold">{selectedSupplier.gst || '-'}</span>
                {selectedSupplier.phone ? <span className="ml-1">• Ph: {selectedSupplier.phone}</span> : ''}
              </span>
            ) : (
              <span className="text-slate-400 italic normal-case text-[9px]">Supplier details...</span>
            )}
          </div>
        </div>
        
        {/* Middle Section (Payment Type) */}
        <div className="w-28 sm:w-36 p-1 px-2 border-r border-slate-200 flex flex-col items-center justify-center shrink-0">
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5">Payment Type</span>
          <select 
            value={paymentType} 
            onChange={e=>setPaymentType(e.target.value)} 
            className="outline-none bg-slate-50 py-0.5 px-2 rounded-lg border border-slate-200 focus:border-[#1e5c5c] text-slate-800 font-black cursor-pointer transition-colors shadow-xs text-center text-xs w-full"
          >
            <option value="Credit">Credit</option>
            <option value="Cash">Cash</option>
            <option value="Mix">Mix</option>
            <option value="Online Attempt 1">Online Attempt 1</option>
            <option value="Online Attempt 2">Online Attempt 2</option>
          </select>
        </div>

      
        {/* Right: GST TAX INVOICE */}
        <div className="w-36 sm:w-44 p-1 px-2 flex flex-col justify-between shrink-0">
          <div className="text-center text-[9px] font-black text-slate-800 border-b border-slate-200 pb-0.5 mb-0.5 tracking-widest">
             GST TAX INVOICE
          </div>
          <div className="space-y-0.5 text-xs font-bold">
            <div className="flex justify-between items-center">
              <span className="text-slate-500 text-[10px]">Vch No:</span>
              <input 
                type="text" 
                value={vchNo} 
                onChange={e=>setVchNo(e.target.value)} 
                className="w-16 text-right outline-none bg-transparent border-b border-slate-200 focus:border-[#1e5c5c] uppercase text-[#1e5c5c] font-black text-xs" 
                placeholder="1" 
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 text-[10px]">Inv No:</span>
              <input 
                type="text" 
                value={invoiceNo} 
                onChange={e=>setInvoiceNo(e.target.value)} 
                className="w-20 text-right outline-none bg-transparent border-b border-slate-200 focus:border-[#1e5c5c] uppercase text-[#1e5c5c] font-black text-xs" 
                placeholder="M3519" 
              />
            </div>
          </div>
        </div>
      </div>


      {/* Main Table Area */}
      <div className="bg-white border-2 border-slate-300 rounded-lg shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden mb-1">
        <div className="overflow-auto w-full flex-1 min-h-0 relative">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead className="sticky top-0 z-20 shadow-xs">
              <tr className="bg-[#1e5c5c] text-white">
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[32px] min-w-[32px] max-w-[32px] text-center border-b border-[#154242] sticky left-0 top-0 z-30 bg-[#1e5c5c]">#</th>
                <th className="py-1 px-2 text-xs font-black uppercase tracking-wider min-w-[170px] border-b border-[#154242] border-l border-white/20 sticky left-[32px] top-0 z-30 bg-[#1e5c5c] shadow-[3px_0_5px_-2px_rgba(0,0,0,0.3)]">Product Name</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[55px] min-w-[55px] border-b border-[#154242] border-l border-white/20 text-center">Packing</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[65px] min-w-[65px] border-b border-[#154242] border-l border-white/20 text-center">Mfg</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[48px] min-w-[48px] border-b border-[#154242] border-l border-white/20 text-center">Qty</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[75px] min-w-[75px] border-b border-[#154242] border-l border-white/20 text-center">Batch</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[55px] min-w-[55px] border-b border-[#154242] border-l border-white/20 text-center">Exp</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[62px] min-w-[62px] border-b border-[#154242] border-l border-white/20 text-right">MRP</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[62px] min-w-[62px] border-b border-[#154242] border-l border-white/20 text-right">Rate</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[48px] min-w-[48px] border-b border-[#154242] border-l border-white/20 text-right" title="Item Discount (₹ Amount)">Disc</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[42px] min-w-[42px] border-b border-[#154242] border-l border-white/20 text-right">Sch</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[44px] min-w-[44px] border-b border-[#154242] border-l border-white/20 text-right">Gst%</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[72px] min-w-[72px] border-b border-[#154242] border-l border-white/20 text-right bg-[#164848]" title="Rate with GST included (Per Unit Cost)">Cost Rate</th>
                <th className="py-1 px-1.5 text-xs font-black uppercase tracking-wider w-[72px] min-w-[72px] border-b border-[#154242] border-l border-white/20 text-right">Amount</th>
                <th className="py-1 px-1 text-xs font-black uppercase tracking-wider w-[42px] min-w-[42px] text-center border-b border-[#154242] border-l border-white/20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((row, index) => (
                <tr key={row.id} onClick={() => setSelectedRowId(row.id)} className={`group transition-colors ${row.id === selectedRowId ? 'bg-teal-50/70 ring-1 ring-[#1e5c5c]' : 'hover:bg-slate-50'}`}>
                  <td className="py-1 px-1.5 text-xs font-bold text-slate-400 text-center border-r border-slate-200 sticky left-0 z-10 bg-white group-hover:bg-slate-100 transition-colors w-[32px] min-w-[32px] max-w-[32px]">{index + 1}</td>
                  
                  <td className="py-1 px-1 border-r border-slate-200 relative min-w-[170px] sticky left-[32px] z-10 bg-white group-hover:bg-slate-100 transition-colors shadow-[3px_0_5px_-2px_rgba(0,0,0,0.08)]">
                    <input 
                      type="text" 
                      list={`product-datalist-${row.id}`}
                      value={row.productName} 
                      onChange={(e) => {
                        const val = e.target.value;
                        updateRow(row.id, 'productName', val);
                        const matched = localMedicines.find(m => m.name.toUpperCase() === val.toUpperCase().trim());
                        if (matched) {
                          selectProduct(matched, row.id);
                        }
                      }}
                      onFocus={() => {
                        setProductSearchQuery(row.productName || '');
                        setProductSuggestionIndex(0);
                      }}
                      onDoubleClick={() => {
                        setActiveProductSearchRow(row.id);
                        setProductSearchQuery(row.productName || '');
                        setProductSuggestionIndex(0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'F2' || e.key === 'Insert') {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveProductRowId(row.id);
                          setShowProductModal(true);
                          return;
                        }
                        if (e.key === 'F4') {
                          e.preventDefault();
                          e.stopPropagation();
                          openEditProductModal(row.productName);
                          return;
                        }
                        if (e.key === 'F3') {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowPastBillsDrawer(true);
                          setInvoiceSearchTerm('');
                          setSelectedBillIndex(0);
                          setTimeout(() => invoiceSearchInputRef.current?.focus(), 60);
                        }
                      }}
                      className="w-full bg-transparent text-xs font-bold text-slate-800 py-1 px-1 outline-none uppercase placeholder:text-slate-300" 
                      placeholder="Type or select product..."
                    />
                    <datalist id={`product-datalist-${row.id}`}>
                      {getAvailableMedicines(row.id).map((m, idx) => (
                        <option key={idx} value={m.name}>{m.company ? `${m.name} (${m.company})` : m.name}</option>
                      ))}
                    </datalist>
                  </td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[55px] min-w-[55px]"><input type="text" value={row.packing} onChange={(e) => updateRow(row.id, 'packing', e.target.value)} className="w-full bg-transparent text-xs font-bold text-slate-800 py-0.5 px-0.5 outline-none uppercase text-center" placeholder="Pack" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[65px] min-w-[65px]"><input type="text" value={row.mfg} onChange={(e) => updateRow(row.id, 'mfg', e.target.value)} className="w-full bg-transparent text-xs font-bold text-slate-800 py-0.5 px-0.5 outline-none uppercase" placeholder="Mfg" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[48px] min-w-[48px]"><input type="number" min="1" value={row.qty} onChange={(e) => updateRow(row.id, 'qty', e.target.value)} className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-center" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[75px] min-w-[75px]"><input type="text" value={row.batch} onChange={(e) => updateRow(row.id, 'batch', e.target.value)} className="w-full bg-transparent text-xs font-bold text-slate-800 py-0.5 px-0.5 outline-none uppercase text-center" placeholder="Batch" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[55px] min-w-[55px]"><input type="text" value={row.exp} placeholder="MM/YY" maxLength={5} onChange={(e) => {
                    let val = e.target.value.replace(/[^\d]/g, '');
                    if (val.length > 2) {
                      val = val.substring(0, 2) + '/' + val.substring(2, 4);
                    }
                    updateRow(row.id, 'exp', val);
                  }} className="w-full bg-transparent text-xs font-bold text-slate-800 py-0.5 px-0.5 outline-none uppercase text-center" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[62px] min-w-[62px]"><input type="number" value={row.mrp} onChange={(e) => updateRow(row.id, 'mrp', e.target.value)} className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-right" placeholder="0" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[62px] min-w-[62px]"><input type="number" value={row.rate} onChange={(e) => updateRow(row.id, 'rate', e.target.value)} className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-right" placeholder="0" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[48px] min-w-[48px]"><input type="number" step="any" value={row.disc} onChange={(e) => updateRow(row.id, 'disc', e.target.value)} className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-right" placeholder="0" title="Item Discount Amount (₹)" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[42px] min-w-[42px]"><input type="text" value={row.sch !== undefined && row.sch !== null && row.sch !== '' ? row.sch : (row.freeQty ? String(row.freeQty) : '')} onChange={(e) => updateRow(row.id, 'sch', e.target.value)} placeholder="0" className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-center" /></td>
                  <td className="py-1 px-1 border-r border-slate-200 w-[44px] min-w-[44px]"><input type="number" value={row.gst} onChange={(e) => updateRow(row.id, 'gst', e.target.value)} placeholder="0" className="w-full bg-slate-50 text-xs font-bold text-slate-800 py-0.5 px-0.5 rounded outline-none focus:ring-1 focus:ring-[#1e5c5c] text-right" /></td>
                  
                  {/* Cost Rate (Rate with GST included) */}
                  <td className="py-1 px-1.5 border-r border-slate-200 bg-teal-50/40 w-[72px] min-w-[72px]">
                    <div className="w-full text-xs font-black text-teal-800 py-0.5 text-right" title="Purchase Rate + GST per unit">
                      ₹{computeRowCostRate(row).toFixed(2)}
                    </div>
                  </td>

                  {/* Amount */}
                  <td className="py-1 px-1.5 border-r border-slate-200 w-[72px] min-w-[72px]">
                    <div className="w-full text-xs font-black text-emerald-700 py-0.5 text-right">
                      {row.amount.toFixed(2)}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="py-1 px-1 text-center w-[42px] min-w-[42px]">
                    <div className="flex items-center justify-center gap-0.5">
                      <button 
                        onClick={() => setActiveProductDetailsRow(row.id)}
                        className="text-slate-400 hover:text-[#1e5c5c] p-0.5 rounded transition-colors"
                        title="Edit Product Details"
                      >
                        <svg className="w-3.5 h-3.5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                      </button>
                      <button 
                        onClick={() => removeRow(row.id)}
                        className="text-slate-400 hover:text-rose-500 p-0.5 rounded transition-colors"
                        disabled={rows.length === 1}
                        title="Remove Row"
                      >
                        <svg className="w-3.5 h-3.5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Auto-add row handles adding new lines */}

        {/* Bottom Totals Section - Designed to match image layout exactly */}
        <div className="border-t-2 border-slate-300 bg-white flex flex-col md:flex-row mt-auto text-xs font-bold text-slate-700">
          
          {/* Left Side: Tax Details */}
          <div className="flex-1 border-r border-slate-300 p-0 flex flex-col">
             {(() => {
               const distinctGsts = Array.from(new Set(validRows.map(r => parseFloat(r.gst) || 0))).sort((a,b)=>a-b);
               const gstRateStr = distinctGsts.length > 0 
                 ? distinctGsts.map(g => `${g}%`).join(', ') 
                 : (taxableAmount > 0 && totalTaxAmount > 0 ? `${Math.round((totalTaxAmount / taxableAmount) * 100)}%` : '0%');
               const extCgst = extractedData && parseFloat(extractedData.cgstAmount) > 0 ? parseFloat(extractedData.cgstAmount).toFixed(2) : null;
               const extSgst = extractedData && parseFloat(extractedData.sgstAmount) > 0 ? parseFloat(extractedData.sgstAmount).toFixed(2) : null;
               const halfTax = (totalTaxAmount / 2).toFixed(2);
               const cgstDisplay = extCgst || halfTax;
               const sgstDisplay = extSgst || halfTax;
               return (
                 <>
                   <div className="flex text-slate-500 border-b border-slate-200 bg-slate-50/50">
                     <div className="flex-1 p-1 text-center uppercase border-r border-slate-200 text-xs">Taxable Amt</div>
                     <div className="flex-1 p-1 text-center uppercase border-r border-slate-200 text-xs">GST %</div>
                     <div className="flex-1 p-1 text-center uppercase border-r border-slate-200 text-xs">CGST + SGST</div>
                     <div className="flex-1 p-1 text-center uppercase text-xs">Total Tax</div>
                   </div>
                   <div className="flex items-center font-bold">
                     <div className="flex-1 p-1 text-center border-r border-slate-200 text-slate-700">₹{taxableAmount.toFixed(2)}</div>
                     <div className="flex-1 p-1 text-center border-r border-slate-200 text-blue-700">{gstRateStr}</div>
                     <div className="flex-1 p-1 text-center border-r border-slate-200 text-slate-600 text-[11px]">
                       {totalTaxAmount > 0 ? `₹${cgstDisplay} + ₹${sgstDisplay}` : '₹0.00'}
                     </div>
                     <div className="flex-1 p-1 text-center text-[#1e5c5c] font-black">₹{totalTaxAmount.toFixed(2)}</div>
                   </div>
                 </>
               );
             })()}
             
             {/* Bottom Left Summary */}
             <div className="mt-auto flex border-t border-slate-300 min-h-[42px] items-stretch">
               <div className="px-3 py-1 border-r border-slate-300 flex items-center gap-2 bg-slate-50/90 shrink-0">
                 <div className="text-[11px] font-bold text-slate-600 bg-white px-2.5 py-0.5 rounded-md border border-slate-200 shadow-xs flex items-center gap-1.5">
                   <span>Total Items:</span>
                   <span className="text-blue-700 font-black text-xs">{totalItems}</span>
                 </div>
                 <div className="text-[11px] font-bold text-slate-600 bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-200 shadow-xs flex items-center gap-1.5">
                   <span>Total Qty:</span>
                   <span className="text-emerald-800 font-black text-xs">{totalQty}</span>
                 </div>
                 {(() => {
                   const activeRow = rows.find(r => r.id === selectedRowId) || rows.find(r => r.productName);
                   if (!activeRow || !activeRow.productName) return null;
                   const cRate = computeRowCostRate(activeRow);
                   return (
                     <div className="text-[11px] font-bold text-teal-800 bg-teal-50 px-2.5 py-0.5 rounded-md border border-teal-300 shadow-xs flex items-center gap-1.5" title={`Selected Product: ${activeRow.productName}`}>
                       <span className="text-teal-700">Cost Rate {activeRow.productName ? `(${activeRow.productName.slice(0, 18)})` : ''}:</span>
                       <span className="text-teal-900 font-black text-xs">₹{cRate.toFixed(2)}</span>
                     </div>
                   );
                 })()}
                </div>
               <div className="p-1 px-3 flex-1 text-xs uppercase tracking-wider flex items-center text-slate-500 italic">
                 Rs. {numberToWords(Math.round(toPay))}
               </div>
               <div className="p-1 w-28 flex flex-col items-center justify-center border-l border-slate-300 text-[10px] text-slate-400">
                  <div className="mb-0.5 text-slate-700 font-bold">Checked By</div>
                  <div className="border-t border-slate-300 w-14 mt-0.5"></div>
               </div>
             </div>
          </div>

          {/* Middle Side: Discount Details */}
            <div className="w-56 md:w-60 border-r border-slate-300 flex flex-col shrink-0">
               <div className="flex justify-between p-1 px-3 border-b border-slate-100 items-center text-[11px] bg-white">
                 <div className="flex items-center gap-1">
                   <span className="text-slate-700 font-bold">Cash Disc</span>
                   <div className="flex items-center bg-teal-50 px-1 py-0.5 rounded border border-teal-300">
                     <input 
                       type="number" 
                       step="any"
                       value={cashDiscPercent} 
                       onChange={(e) => {
                         const pct = e.target.value;
                         setCashDiscPercent(pct);
                         setIsManualCashDisc(true);
                         setLastModifiedCashDiscField('percent');
                         if (pct === '' || isNaN(parseFloat(pct))) {
                           setCashDisc('');
                           return;
                         }
                         const numPct = parseFloat(pct) || 0;
                         const base = baseForBillDiscount;
                         if (base > 0) {
                           const amt = ((base * (numPct / 100))).toFixed(2);
                           setCashDisc(amt);
                         }
                       }} 
                       className="w-11 text-right outline-none bg-white border border-teal-400 focus:ring-1 focus:ring-teal-600 rounded px-1 text-[#1e5c5c] font-black text-[11px]" 
                       placeholder="0"
                       title="Cash Discount % (टक्के)"
                     />
                     <span className="text-[10px] font-black text-[#1e5c5c] ml-0.5">%</span>
                   </div>
                 </div>
                 <div className="flex items-center gap-0.5">
                   <span className="text-[10px] text-slate-400 font-bold">₹</span>
                   <input 
                     type="number" 
                     step="any"
                     value={cashDisc} 
                     onChange={(e) => {
                       const val = e.target.value;
                       setCashDisc(val);
                       setIsManualCashDisc(true);
                       setLastModifiedCashDiscField('amount');
                       if (val === '' || isNaN(parseFloat(val))) {
                         setCashDiscPercent('0');
                         return;
                       }
                       const numVal = parseFloat(val) || 0;
                       const base = baseForBillDiscount;
                       if (base > 0 && numVal > 0) {
                         const pct = Math.round((numVal / base) * 100 * 10) / 10;
                         setCashDiscPercent(String(pct));
                       } else if (numVal === 0) {
                         setCashDiscPercent('0');
                       }
                     }} 
                     className="w-20 text-right bg-white border border-slate-300 focus:border-[#1e5c5c] focus:ring-1 focus:ring-[#1e5c5c] rounded px-1.5 py-0.5 font-black text-slate-900 text-xs shadow-2xs transition-all" 
                     placeholder="0.00"
                     title="Cash Discount Amount (रुपये)"
                   />
                 </div>
               </div>
               
               <div className="flex justify-between p-1 px-3 border-b border-slate-100 items-center text-[11px] bg-white">
                 <span className="text-slate-700 flex items-center gap-1 font-bold">
                   <span>Item Disc</span>
                   {grossAmount > 0 && itemDiscVal > 0 && (
                     <span className="text-[9px] text-teal-700 font-bold bg-teal-50 px-1 rounded border border-teal-200">
                       {((itemDiscVal / grossAmount) * 100).toFixed(1)}%
                     </span>
                   )}
                 </span>
                 <div className="flex items-center gap-0.5">
                   <span className="text-[10px] text-slate-400 font-bold">₹</span>
                   <input 
                     type="number" 
                     step="any"
                     value={itemDisc} 
                     onChange={e => setItemDisc(e.target.value)} 
                     onFocus={e => { if (e.target.value === '0' || e.target.value === 0) setItemDisc(''); }}
                     onBlur={e => { if (e.target.value === '') setItemDisc(0); }}
                     className="w-20 text-right bg-white border border-slate-300 focus:border-[#1e5c5c] focus:ring-1 focus:ring-[#1e5c5c] rounded px-1.5 py-0.5 font-bold text-slate-800 text-xs shadow-2xs transition-all" 
                     placeholder="0.00"
                   />
                 </div>
               </div>

               <div className="flex justify-between p-1 px-3 border-b border-slate-100 items-center text-[11px] bg-white">
                 <span className="text-slate-700 flex items-center gap-1 font-bold">
                   <span>Sch. Disc</span>
                   {grossAmount > 0 && schDiscVal > 0 && (
                     <span className="text-[9px] text-teal-700 font-bold bg-teal-50 px-1 rounded border border-teal-200">
                       {((schDiscVal / grossAmount) * 100).toFixed(1)}%
                     </span>
                   )}
                 </span>
                 <div className="flex items-center gap-0.5">
                   <span className="text-[10px] text-slate-400 font-bold">₹</span>
                   <input 
                     type="number" 
                     step="any"
                     value={schDisc} 
                     onChange={e => setSchDisc(e.target.value)} 
                     onFocus={e => { if (e.target.value === '0' || e.target.value === 0) setSchDisc(''); }}
                     onBlur={e => { if (e.target.value === '') setSchDisc(0); }}
                     className="w-20 text-right bg-white border border-slate-300 focus:border-[#1e5c5c] focus:ring-1 focus:ring-[#1e5c5c] rounded px-1.5 py-0.5 font-bold text-slate-800 text-xs shadow-2xs transition-all" 
                     placeholder="0.00"
                   />
                 </div>
               </div>

              {/* Less Amount Section with 2 Methods (GST आधी vs GST नंतर) */}
              <div className="p-1 px-3 border-b border-slate-100 bg-slate-50/50">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <span>Less Amt</span>
                    <span className="text-[8px] text-teal-800 font-black bg-teal-100/80 px-1 py-0.2 rounded border border-teal-300">
                      २ पद्धती
                    </span>
                  </span>
                  
                  {/* Mode Selector Toggle: GST आधी vs GST नंतर vs दोन्ही */}
                  <div className="flex bg-white rounded p-0.5 border border-slate-200 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setLessMode('PRE_TAX')}
                      className={`px-1.5 py-0.5 text-[9px] font-black rounded transition-all ${
                        lessMode === 'PRE_TAX' 
                          ? 'bg-[#1e5c5c] text-white shadow-xs' 
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                      title="पद्धत १: GST पूर्वी Less (Gross मधून वजा होऊन Taxable रक्कम ठरते)"
                    >
                      GST आधी
                    </button>
                    <button
                      type="button"
                      onClick={() => setLessMode('POST_TAX')}
                      className={`px-1.5 py-0.5 text-[9px] font-black rounded transition-all ${
                        lessMode === 'POST_TAX' 
                          ? 'bg-amber-600 text-white shadow-xs' 
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                      title="पद्धत २: GST नंतर Less (GST जोडल्यानंतर Final Bill मधून थेट वजा होते)"
                    >
                      GST नंतर
                    </button>
                    <button
                      type="button"
                      onClick={() => setLessMode('BOTH')}
                      className={`px-1 py-0.5 text-[9px] font-black rounded transition-all ${
                        lessMode === 'BOTH' 
                          ? 'bg-indigo-600 text-white shadow-xs' 
                          : 'text-slate-400 hover:text-slate-700'
                      }`}
                      title="दोन्ही पद्धती (GST आधी आणि GST नंतर दोन्ही Less रकमा)"
                    >
                      दोन्ही
                    </button>
                  </div>
                </div>

                {/* Primary Less Input */}
                <div className="flex justify-between items-center text-[11px]">
                  <span className="text-slate-600 font-bold flex items-center gap-1">
                    <span className="text-[10px]">
                      {lessMode === 'POST_TAX' 
                        ? 'Less (GST नंतर):' 
                        : lessMode === 'BOTH' 
                          ? 'Less १ (GST आधी):' 
                          : 'Less (GST आधी):'}
                    </span>
                    {grossAmount > 0 && lessAmountVal > 0 && (
                      <span className="text-[8px] text-rose-700 font-bold bg-rose-50 px-1 rounded border border-rose-200">
                        {((lessAmountVal / grossAmount) * 100).toFixed(1)}%
                      </span>
                    )}
                    {breakdownDiscounts > 0 && Math.abs(parseFloat(lessAmount || 0) - breakdownDiscounts) > 0.05 && (
                      <span 
                        onClick={() => setLessAmount(breakdownDiscounts.toFixed(2))} 
                        className="text-[9px] text-teal-700 font-bold bg-teal-50 px-1 rounded border border-teal-200 cursor-pointer hover:bg-teal-100 transition-colors ml-1"
                        title="Click to sync Total Less with Cash + Scheme Disc sum"
                      >
                        Sync ₹{breakdownDiscounts.toFixed(2)}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <span className="text-[10px] text-slate-400 font-bold">₹</span>
                    <input 
                      type="number" 
                      step="any"
                      value={lessAmount} 
                      onChange={e => {
                        const val = e.target.value;
                        setLessAmount(val);
                      }} 
                      className="w-20 text-right bg-white border border-rose-300 focus:border-rose-600 focus:ring-1 focus:ring-rose-500 rounded px-1.5 py-0.5 font-black text-rose-700 text-xs shadow-2xs" 
                      placeholder="0.00" 
                    />
                  </div>
                </div>

                {/* Secondary Less Input if 'BOTH' is selected */}
                {lessMode === 'BOTH' && (
                  <div className="flex justify-between items-center text-[11px] mt-1 pt-1 border-t border-slate-200">
                    <span className="text-amber-800 font-bold text-[10px]">
                      Less २ (GST नंतर):
                    </span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-[10px] text-slate-400 font-bold">₹</span>
                      <input 
                        type="number" 
                        step="any"
                        value={postTaxLess} 
                        onChange={e => setPostTaxLess(e.target.value)} 
                        className="w-20 text-right bg-white border border-amber-300 focus:border-amber-600 focus:ring-1 focus:ring-amber-500 rounded px-1.5 py-0.5 font-black text-amber-700 text-xs shadow-2xs" 
                        placeholder="0.00" 
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between p-1 px-3 border-b border-slate-100 items-center text-[11px] bg-white">
                <span className="text-slate-700 flex items-center gap-1 font-bold">
                  <span>Others</span>
                  {grossAmount > 0 && othersVal > 0 && (
                    <span className="text-[9px] text-teal-700 font-bold bg-teal-50 px-1 rounded border border-teal-200">
                      {((othersVal / grossAmount) * 100).toFixed(1)}%
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-0.5">
                  <span className="text-[10px] text-slate-400 font-bold">₹</span>
                  <input 
                    type="number" 
                    step="any"
                    value={others} 
                    onChange={e => setOthers(e.target.value)} 
                    onFocus={e => { if (e.target.value === '0' || e.target.value === 0) setOthers(''); }}
                    onBlur={e => { if (e.target.value === '') setOthers(0); }}
                    className="w-20 text-right bg-white border border-slate-300 focus:border-[#1e5c5c] focus:ring-1 focus:ring-[#1e5c5c] rounded px-1.5 py-0.5 font-bold text-slate-800 text-xs shadow-2xs transition-all" 
                    placeholder="0.00"
                    title="Others (इतर सूट / कपात)"
                  />
                </div>
              </div>
              
              <div className="flex justify-between p-1 px-3 text-[#1e5c5c] font-black h-8 items-center bg-teal-50/30">
                <span className="uppercase text-xs">Gross</span>
                <span className="text-xs">₹{grossAmount.toFixed(2)}</span>
              </div>
           </div>

           {/* Right Side: Final Totals */}
           <div className="w-56 md:w-60 flex flex-col justify-between bg-slate-50/80 border-l border-slate-200 shrink-0">
              <div className="p-1 px-3 space-y-0.5 text-[11px]">
                {totalRowDiscs > 0 && (
                  <div className="flex justify-between text-slate-500 font-semibold">
                    <span>- Item Disc (वरचा):</span>
                    <span className="font-mono text-rose-600">-₹{totalRowDiscs.toFixed(2)}</span>
                  </div>
                )}
                {effectivePreTaxDiscounts > 0 && (
                  <div className="flex justify-between text-rose-600 font-semibold">
                    <span className="flex items-center gap-1">
                      <span>- Discount / Less:</span>
                      {baseForBillDiscount > 0 && (
                        <span className="text-[9px] bg-rose-100 text-rose-700 font-bold px-1 rounded">
                          {((effectivePreTaxDiscounts / baseForBillDiscount) * 100).toFixed(1)}%
                        </span>
                      )}
                    </span>
                    <span className="font-mono">₹{effectivePreTaxDiscounts.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600 font-semibold">
                  <span>Taxable (करपात्र):</span>
                  <span className="font-mono">₹{taxableAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-emerald-700 font-semibold">
                  <span>+ Total GST (कर):</span>
                  <span className="font-mono">₹{totalTaxAmount.toFixed(2)}</span>
                </div>
                {finalPostTaxLess > 0 && (
                  <div className="flex justify-between text-amber-800 font-bold bg-amber-50 px-1 rounded border border-amber-200">
                    <span>- Less (GST नंतर):</span>
                    <span className="font-mono">₹{finalPostTaxLess.toFixed(2)}</span>
                  </div>
                )}
                {parseFloat(roundOff) !== 0 && (
                  <div className="flex justify-between text-slate-500 font-semibold">
                    <span>Round Off:</span>
                    <span className="font-mono">{parseFloat(roundOff) > 0 ? `+₹${roundOff}` : `-₹${Math.abs(parseFloat(roundOff)).toFixed(2)}`}</span>
                  </div>
                )}
                {parseFloat(addAmountLocal || 0) > 0 && (
                  <div className="flex justify-between text-blue-600 font-semibold">
                    <span>+ Add:</span>
                    <span className="font-mono">₹{parseFloat(addAmountLocal).toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="h-9 flex border-t border-slate-300">
                <div className="flex-1 flex justify-between items-center px-3 bg-[#1e5c5c] text-white shadow-inner">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs uppercase tracking-wider font-bold">To Pay</span>
                    {isManualToPay && (
                      <button 
                        type="button"
                        onClick={() => { setIsManualToPay(false); setManualToPay(''); }}
                        title="Reset to Auto Calculated Total"
                        className="text-[10px] bg-teal-800 hover:bg-teal-700 px-1.5 py-0.5 rounded text-teal-200 border border-teal-600 transition-colors"
                      >
                        Auto ↺
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-teal-200">₹</span>
                    <input 
                      type="number"
                      step="0.01"
                      value={isManualToPay ? manualToPay : toPay.toFixed(2)}
                      onChange={(e) => {
                        setIsManualToPay(true);
                        setManualToPay(e.target.value);
                      }}
                      className="w-24 bg-transparent text-right text-base font-black text-white outline-none border-b border-teal-400 focus:border-white focus:bg-teal-800/50 rounded px-1"
                      title="Editable To Pay (Matches Bill Image exactly)"
                    />
                  </div>
                </div>
              </div>
           </div>

        </div>
      </div>
      
      {/* Save Button outside the invoice layout */}
      <div className="flex justify-end items-center gap-2 shrink-0 mt-1">
        <button 
          onClick={handleSaveBill}
          disabled={isSaving}
          className={`w-64 py-1 text-xs font-black text-white rounded-lg shadow-md transition-all flex items-center justify-center gap-2 ${isSaving ? 'bg-slate-400 cursor-not-allowed' : (editingBillId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#1e5c5c] hover:bg-[#154242]')}`}
        >
          {isSaving ? 'Saving...' : (editingBillId ? 'UPDATE GST INVOICE' : 'SAVE GST INVOICE')}
        </button>
      </div>
        </div>
      </div>

      {/* Add Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-5 py-1 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="text-base font-black text-[#1e5c5c]">Add New Supplier</h3>
              <button onClick={() => setShowSupplierModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-2 space-y-1">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Supplier Name</label>
                <input type="text" value={newSupplier.name} onChange={e => setNewSupplier({...newSupplier, name: e.target.value})} className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-3 py-1 outline-none focus:border-[#1e5c5c]" placeholder="Enter supplier name" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Address</label>
                <textarea value={newSupplier.address} onChange={e => setNewSupplier({...newSupplier, address: e.target.value})} className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-3 py-1 outline-none focus:border-[#1e5c5c] resize-none h-20" placeholder="Enter full address"></textarea>
              </div>
              
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 mb-1">Phone</label>
                  <input type="text" value={newSupplier.phone} onChange={e => setNewSupplier({...newSupplier, phone: e.target.value})} className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-3 py-1 outline-none focus:border-[#1e5c5c]" placeholder="Phone" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 mb-1">GSTIN</label>
                  <input type="text" value={newSupplier.gst} onChange={e => setNewSupplier({...newSupplier, gst: e.target.value})} className="w-full bg-white border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-3 py-1 outline-none focus:border-[#1e5c5c] uppercase" placeholder="GSTIN" />
                </div>
              </div>
            </div>
            
            <div className="px-5 py-1 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowSupplierModal(false)} className="px-4 py-1 text-xs font-bold text-slate-500 hover:bg-slate-200 rounded-lg">Cancel</button>
              <button onClick={handleSaveSupplier} className="px-4 py-1 text-xs font-bold text-white bg-[#1e5c5c] hover:bg-[#154242] rounded-lg shadow-sm">Save Supplier</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal (F3) */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-5 py-1 border-b border-slate-100 flex items-center justify-between bg-[#1e5c5c]">
              <h3 className="text-base font-black text-white flex items-center gap-2">
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded font-black">F2</span>
                <span>Add New Product (नवीन प्रॉडक्ट ऍड करा)</span>
              </h3>
              <button onClick={() => setShowProductModal(false)} className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-2">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 text-xs font-bold uppercase">
                    <th className="p-2 border border-slate-200">Product Name</th>
                    <th className="p-2 border border-slate-200">Company</th>
                    <th className="p-2 border border-slate-200">Category</th>
                    <th className="p-2 border border-slate-200">Unit of Major</th>
                    <th className="p-2 border border-slate-200">Strip</th>
                    <th className="p-2 border border-slate-200">GST%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-2 border border-slate-200 relative">
                      <input 
                        type="text" 
                        value={newProduct.name} 
                        onChange={e => {
                          setNewProduct({...newProduct, name: e.target.value});
                        }} 
                        className="w-full bg-transparent outline-none font-bold text-xs text-slate-800" 
                        placeholder="Enter Name" 
                        autoFocus 
                      />
                    </td>
                    <td className="p-2 border border-slate-200">
                      <input type="text" value={newProduct.company} onChange={e => setNewProduct({...newProduct, company: e.target.value})} className="w-full bg-transparent outline-none font-bold text-xs text-slate-800" placeholder="Enter Company" />
                    </td>
                    <td className="p-2 border border-slate-200">
                      <select 
                        value={newProduct.category || 'Human Medicine'} 
                        onChange={e => setNewProduct({...newProduct, category: e.target.value})} 
                        className="w-full bg-transparent outline-none font-bold text-xs text-slate-800 cursor-pointer"
                      >
                        <option value="Human Medicine">Human Medicine</option>
                        <option value="Vet Medicine">Vet Medicine</option>
                        <option value="General">General</option>
                      </select>
                    </td>
                    <td className="p-2 border border-slate-200">
                      <input type="text" value={newProduct.major} onChange={e => setNewProduct({...newProduct, major: e.target.value})} className="w-full bg-transparent outline-none font-bold text-xs text-slate-800" placeholder="Unit of Major" />
                    </td>
                    <td className="p-2 border border-slate-200">
                      <input type="text" value={newProduct.strip} onChange={e => setNewProduct({...newProduct, strip: e.target.value})} className="w-full bg-transparent outline-none font-bold text-xs text-slate-800" placeholder="Enter Strip" />
                    </td>
                    <td className="p-2 border border-slate-200">
                      <select 
                        value={newProduct.gst} 
                        onChange={e => setNewProduct({...newProduct, gst: e.target.value})} 
                        className="w-full bg-transparent outline-none font-bold text-xs text-slate-800 cursor-pointer"
                      >
                        <option value="">Select GST%</option>
                        <option value="0">0%</option>
                        <option value="5">5%</option>
                        <option value="12">12%</option>
                        <option value="18">18%</option>
                        <option value="28">28%</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="px-5 py-1 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowProductModal(false)} className="px-4 py-1 text-xs font-bold text-slate-500 hover:bg-slate-200 rounded-lg">Cancel</button>
              <button onClick={() => {
                if(newProduct.name.trim()){
                   const prodToSave = { ...newProduct, category: newProduct.category || 'Human Medicine' };
                   // Save to localStorage so it persists to Sales.jsx
                   const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
                   custom.push(prodToSave);
                   localStorage.setItem('customMedicines', JSON.stringify(custom));
                   
                   setLocalMedicines([...localMedicines, prodToSave].sort((a, b) => a.name.localeCompare(b.name)));
                   
                   let targetRowId = activeProductRowId;
                   if (!targetRowId) {
                     const emptyRow = rows.find(r => r.productName.trim() === '');
                     if (emptyRow) {
                       targetRowId = emptyRow.id;
                     } else if (rows.length > 0) {
                       targetRowId = rows[rows.length - 1].id;
                     }
                   }
                   
                   if (targetRowId) {
                     updateRow(targetRowId, 'productName', prodToSave.name);
                   }
                   
                   setNewProduct({ name: '', company: '', major: '', strip: '', gst: '', category: 'Human Medicine' });
                   setShowProductModal(false);
                   setActiveProductRowId(null);
                } else {
                   alert('Product Name is required!');
                }
              }} className="px-4 py-1 text-xs font-bold text-white bg-[#1e5c5c] hover:bg-[#154242] rounded-lg shadow-sm">Save Product</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal (F4) */}
      {showEditProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100">
            <div className="px-5 py-2.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-indigo-700 via-indigo-800 to-indigo-900 text-white">
              <div className="flex items-center gap-2">
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded font-black">F4</span>
                <h3 className="text-base font-black">Edit Product (प्रॉडक्ट एडिट करा)</h3>
              </div>
              <button 
                onClick={() => setShowEditProductModal(false)} 
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Close (Esc)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-4 space-y-3">
              {/* Product Search / Selector Bar */}
              <div className="bg-indigo-50/60 p-2.5 rounded-xl border border-indigo-100">
                <label className="text-[11px] font-black text-indigo-900 uppercase tracking-wider block mb-1">
                  🔍 निवडा किंवा शोधा (Select Existing Product to Edit):
                </label>
                <div className="relative">
                  <input 
                    type="text"
                    placeholder="Search product from catalog..."
                    value={editProductSearchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditProductSearchTerm(val);
                      const match = localMedicines.find(m => m.name.toUpperCase() === val.toUpperCase().trim());
                      if (match) {
                        setEditingProductOriginalName(match.name);
                        setEditProductData({
                          name: match.name,
                          company: match.company || match.mfg || '',
                          category: match.category || 'Human Medicine',
                          packing: match.packing || match.strip || '',
                          mrp: match.mrp !== undefined && match.mrp !== '' ? String(match.mrp) : '',
                          rate: match.rate !== undefined && match.rate !== '' ? String(match.rate) : (match.purchaseRate ? String(match.purchaseRate) : ''),
                          gst: match.gst !== undefined ? String(match.gst) : '0'
                        });
                      }
                    }}
                    list="edit-product-catalog-datalist"
                    className="w-full bg-white text-xs font-bold text-slate-800 py-1.5 px-3 border border-indigo-200 rounded-lg outline-none focus:border-indigo-500 uppercase shadow-xs"
                  />
                  <datalist id="edit-product-catalog-datalist">
                    {localMedicines.slice(0, 100).map((m, idx) => (
                      <option key={idx} value={m.name}>
                        {m.company ? `${m.name} (${m.company})` : m.name}
                      </option>
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Editable Fields Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                <div className="col-span-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Product Name *</label>
                  <input 
                    type="text" 
                    value={editProductData.name} 
                    onChange={e => setEditProductData({...editProductData, name: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 uppercase focus:bg-white" 
                    placeholder="Product Name" 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Category</label>
                  <select 
                    value={editProductData.category} 
                    onChange={e => setEditProductData({...editProductData, category: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 cursor-pointer focus:bg-white"
                  >
                    <option value="Human Medicine">Human Medicine</option>
                    <option value="Vet Medicine">Vet Medicine</option>
                    <option value="General">General</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Company / Mfg</label>
                  <input 
                    type="text" 
                    value={editProductData.company} 
                    onChange={e => setEditProductData({...editProductData, company: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 uppercase focus:bg-white" 
                    placeholder="Mfg / Company" 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Packing / Strip</label>
                  <input 
                    type="text" 
                    value={editProductData.packing} 
                    onChange={e => setEditProductData({...editProductData, packing: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 uppercase focus:bg-white" 
                    placeholder="e.g. 10T, 100ml" 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">GST %</label>
                  <select 
                    value={editProductData.gst} 
                    onChange={e => setEditProductData({...editProductData, gst: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 cursor-pointer focus:bg-white"
                  >
                    <option value="0">0%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">MRP (₹)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={editProductData.mrp} 
                    onChange={e => setEditProductData({...editProductData, mrp: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white" 
                    placeholder="0.00" 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Purchase Rate (₹)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={editProductData.rate} 
                    onChange={e => setEditProductData({...editProductData, rate: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2.5 text-xs font-bold text-slate-800 outline-none focus:border-indigo-500 focus:bg-white" 
                    placeholder="0.00" 
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-teal-700 uppercase tracking-wider block mb-1">Cost Rate (With GST)</label>
                  <div className="w-full bg-teal-50 border border-teal-200 rounded-lg py-1.5 px-2.5 text-xs font-black text-teal-800">
                    ₹{(() => {
                      const r = parseFloat(editProductData.rate) || 0;
                      const g = parseFloat(editProductData.gst) || 0;
                      return (r * (1 + g / 100)).toFixed(2);
                    })()}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400 font-bold">
                {editingProductOriginalName ? `Editing: ${editingProductOriginalName}` : 'Select a product to edit'}
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowEditProductModal(false)} 
                  className="px-4 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateProduct} 
                  className="px-4 py-1.5 text-xs font-black text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-lg shadow-sm transition-all flex items-center gap-1.5"
                >
                  <span>💾 Update Product</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPastBillsDrawer && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={() => setShowPastBillsDrawer(false)}
          ></div>
          
          {/* Drawer Panel */}
          <div className="absolute inset-y-0 right-0 max-w-md w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
            {/* Drawer Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white font-black text-sm shadow-md">
                  F3
                </div>
                <div>
                  <h2 className="text-base font-black text-slate-800 flex items-center gap-1.5">
                    Edit Purchase Bill
                    <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">बिल एडिट करा</span>
                  </h2>
                  <p className="text-[11px] font-bold text-slate-500">
                    Press <span className="bg-amber-200 text-amber-900 px-1 rounded font-black">F3</span> or <span className="bg-slate-200 text-slate-700 px-1 rounded font-black">Enter</span> to edit selected bill
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowPastBillsDrawer(false)}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-full transition-colors"
                title="Close (Esc)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="px-4 py-3 border-b border-slate-100 bg-white">
              <div className="relative">
                <input 
                  ref={invoiceSearchInputRef}
                  type="text" 
                  placeholder="Search by Invoice No, Supplier... (↑/↓ to navigate, F3 to edit)"
                  value={invoiceSearchTerm}
                  onChange={(e) => {
                    setInvoiceSearchTerm(e.target.value);
                    setSelectedBillIndex(0);
                  }}
                  className="w-full text-xs font-bold text-slate-800 py-2 pl-8 pr-3 border-2 border-slate-200 rounded-lg outline-none focus:border-amber-500 transition-colors"
                />
                <span className="absolute left-2.5 top-2.5 text-slate-400 text-xs">🔍</span>
              </div>
            </div>
            
            {/* Drawer Body - List of Bills */}
            <div className="flex-1 overflow-y-auto p-2 space-y-2.5 bg-slate-50/50">
              {filteredPastBills.length === 0 ? (
                <div className="text-center text-slate-400 py-10 text-xs font-bold">No past bills found.</div>
              ) : (
                filteredPastBills.map((bill, idx) => {
                  const isSelected = idx === selectedBillIndex;
                  return (
                    <div 
                      key={bill.id}
                      onClick={() => loadBillForEditing(bill)}
                      onMouseEnter={() => setSelectedBillIndex(idx)}
                      className={`rounded-xl p-2.5 cursor-pointer transition-all border-2 ${
                        isSelected 
                          ? 'bg-amber-50/70 border-amber-500 shadow-md ring-2 ring-amber-400/30' 
                          : 'bg-white border-slate-200 hover:border-amber-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1.5">
                        <div className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                          {isSelected && <span className="text-amber-600 font-black text-xs">▶</span>}
                          <span>{bill.supplierName || 'Unknown Supplier'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{bill.date}</div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePurchaseBill(bill.id);
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors cursor-pointer"
                            title="Delete this purchase bill"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 my-1">
                        {bill.billImage ? (
                          <span className="text-[10px] font-black bg-teal-100 text-teal-800 border border-teal-300 px-1.5 py-0.5 rounded flex items-center gap-1">
                            📷 Photo Attached
                          </span>
                        ) : null}
                        {parseFloat(bill.lessAmount || 0) > 0 && (
                          <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                            Less: -₹{parseFloat(bill.lessAmount).toFixed(1)}
                          </span>
                        )}
                        {parseFloat(bill.totalTaxAmount || 0) > 0 && (
                          <span className="text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded">
                            GST: +₹{parseFloat(bill.totalTaxAmount).toFixed(1)}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-1 border-t border-slate-100">
                        <div className="text-xs font-bold text-slate-500">
                          Inv: <span className="text-slate-800 font-black">{bill.invoiceNo || '-'}</span>
                          <span className="text-emerald-700 font-black ml-2">₹{parseFloat(bill.netAmount || bill.grossAmount || 0).toFixed(2)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            loadBillForEditing(bill);
                          }}
                          className={`px-2.5 py-1 text-xs font-black rounded-lg transition-all flex items-center gap-1 shadow-xs ${
                            isSelected 
                              ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-200' 
                              : 'bg-amber-50 hover:bg-amber-500 text-amber-800 hover:text-white border border-amber-300'
                          }`}
                        >
                          <span className="text-[10px] opacity-80">[F3]</span>
                          <span>Edit Bill</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      
      {activeProductSearchRow && (() => {
        const getFilteredMedicines = () => {
          return localMedicines.filter(m => {
            const q = productSearchQuery.toLowerCase();
            const matchesQuery = m.name.toLowerCase().includes(q) || (m.company && m.company.toLowerCase().includes(q));
            if (selectedSearchCategory === 'HUMAN') {
              return matchesQuery && (m.category || '').toLowerCase().includes('human');
            }
            if (selectedSearchCategory === 'VET') {
              return matchesQuery && ((m.category || '').toLowerCase().includes('vet') || !m.category);
            }
            return matchesQuery;
          });
        };

        const filteredList = getFilteredMedicines();

        return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setActiveProductSearchRow(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col h-[70vh] max-h-[600px] overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-100" onMouseDown={e => e.stopPropagation()}>
             {/* Header with Search Input */}
             <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center gap-3 shrink-0">
               <div className="p-2 bg-[#1e5c5c]/10 text-[#1e5c5c] rounded-full">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
               </div>
               <input 
                 autoFocus
                 type="text"
                 value={productSearchQuery}
                 onChange={e => { setProductSearchQuery(e.target.value); setProductSuggestionIndex(0); }}
                 onKeyDown={(e) => {
                   if (e.key === 'ArrowDown') {
                     e.preventDefault();
                     setProductSuggestionIndex(Math.min(productSuggestionIndex + 1, filteredList.length - 1));
                   } else if (e.key === 'ArrowUp') {
                     e.preventDefault();
                     setProductSuggestionIndex(Math.max(0, productSuggestionIndex - 1));
                   } else if (e.key === 'Enter') {
                     e.preventDefault();
                     if (filteredList.length > 0) {
                       const m = filteredList[Math.max(0, productSuggestionIndex)];
                       selectProduct(m, activeProductSearchRow);
                     } else if (productSearchQuery.trim()) {
                       const newNameUpper = productSearchQuery.trim().toUpperCase();
                       updateRow(activeProductSearchRow, 'productName', newNameUpper);

                       const custom = JSON.parse(localStorage.getItem('customMedicines') || '[]');
                       if (!custom.some(m => m.name.toUpperCase() === newNameUpper) && !vetMedicines.some(m => m.name.toUpperCase() === newNameUpper) && !humanMedicines.some(m => m.name.toUpperCase() === newNameUpper)) {
                         custom.push({ name: newNameUpper, company: 'PHARMA', category: 'Human Medicine' });
                         localStorage.setItem('customMedicines', JSON.stringify(custom));
                       }
                       setLocalMedicines(prev => {
                         const combinedMap = new Map();
                         [...prev, ...custom, { name: newNameUpper, company: 'PHARMA', category: 'Human Medicine' }].forEach(m => {
                           if (m && m.name) {
                             const key = m.name.toUpperCase().trim();
                             if (!combinedMap.has(key)) combinedMap.set(key, { name: key, company: m.company || m.mfg || '', category: m.category || 'General' });
                           }
                         });
                         return Array.from(combinedMap.values()).sort((a, b) => a.name.localeCompare(b.name));
                       });

                       setActiveProductSearchRow(null);
                     }
                   } else if (e.key === 'Escape') {
                     setActiveProductSearchRow(null);
                   }
                 }}
                 placeholder="Search products by name or type new..."
                 className="w-full bg-transparent text-lg font-black text-slate-800 outline-none uppercase placeholder:text-slate-300 placeholder:normal-case placeholder:font-bold"
               />
               <button onClick={() => setActiveProductSearchRow(null)} className="p-2 text-slate-400 hover:text-rose-500 rounded-full transition-colors bg-white shadow-sm border border-slate-100 hover:bg-rose-50 ml-auto shrink-0">
                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
               </button>
             </div>
             
             {/* Category Filter Tabs */}
             <div className="px-4 py-2 bg-slate-100/80 border-b border-slate-200 flex items-center gap-2 shrink-0">
               <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider mr-1">Category:</span>
               <button 
                 type="button"
                 onClick={() => { setSelectedSearchCategory('ALL'); setProductSuggestionIndex(0); }}
                 className={`px-3 py-1 rounded-lg text-xs font-black transition-all ${selectedSearchCategory === 'ALL' ? 'bg-[#1e5c5c] text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-200'}`}
               >
                 ALL ({localMedicines.length})
               </button>
               <button 
                 type="button"
                 onClick={() => { setSelectedSearchCategory('HUMAN'); setProductSuggestionIndex(0); }}
                 className={`px-3 py-1 rounded-lg text-xs font-black transition-all ${selectedSearchCategory === 'HUMAN' ? 'bg-sky-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-sky-50'}`}
               >
                 HUMAN MEDICINES ({localMedicines.filter(m => (m.category || '').toLowerCase().includes('human')).length})
               </button>
               <button 
                 type="button"
                 onClick={() => { setSelectedSearchCategory('VET'); setProductSuggestionIndex(0); }}
                 className={`px-3 py-1 rounded-lg text-xs font-black transition-all ${selectedSearchCategory === 'VET' ? 'bg-rose-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-rose-50'}`}
               >
                 VET MEDICINES ({localMedicines.filter(m => (m.category || '').toLowerCase().includes('vet') || !m.category).length})
               </button>
             </div>

             {/* Results List */}
             <div className="flex-1 overflow-y-auto p-2 bg-slate-50/50">
                {filteredList.length > 0 ? (
                  filteredList.map((m, idx) => {
                      let totalPurchased = 0;
                      let fromCurrentSupplier = 0;
                      let lastCostRate = 0;
                      let lastRate = m.rate || 0;
                      if (allPurchases) {
                        allPurchases.forEach(bill => {
                          const isCurrentSupplier = selectedSupplier && bill.supplierName?.toUpperCase() === selectedSupplier.name?.toUpperCase();
                          (bill.items || []).forEach(item => {
                            if (item.productName?.toUpperCase() === m.name.toUpperCase()) {
                              const q = parseFloat(item.qty) || 0;
                              totalPurchased += q;
                              if (isCurrentSupplier) fromCurrentSupplier += q;
                              if (!lastCostRate) {
                                lastCostRate = computeRowCostRate(item);
                                if (item.rate) lastRate = item.rate;
                              }
                            }
                          });
                        });
                      }
                      if (!lastCostRate && (m.rate || m.costRate || m.costPrice)) {
                        lastCostRate = parseFloat(m.costRate || m.costPrice) || (parseFloat(m.rate) * (1 + (parseFloat(m.gst || 0) / 100)));
                      }
                      
                      const isSelected = productSuggestionIndex === idx;
                      const isHuman = (m.category || '').toLowerCase().includes('human');

                      return (
                        <div 
                          key={idx}
                          className={`px-4 py-3 mb-1 rounded-xl cursor-pointer border transition-all flex justify-between items-center group/item ${isSelected ? 'bg-[#1e5c5c]/10 border-[#1e5c5c]/30 shadow-sm' : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50'}`}
                          onMouseEnter={() => setProductSuggestionIndex(idx)}
                          onClick={() => selectProduct(m, activeProductSearchRow)}
                        >
                          <div>
                            <div className={`font-black text-sm transition-colors flex items-center gap-2 ${isSelected ? 'text-[#1e5c5c]' : 'text-slate-800 group-hover/item:text-[#1e5c5c]'}`}>
                              <span>{m.name}</span>
                              <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                isHuman ? 'bg-sky-100 text-sky-700 border border-sky-200' : 'bg-rose-100 text-rose-700 border border-rose-200 shadow-xs'
                              }`}>
                                {isHuman ? 'HUMAN' : '🔴 VET'}
                              </span>
                            </div>
                            <div className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-1 flex gap-3 items-center">
                              <span>{m.company || 'Unknown Company'}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5 flex-wrap justify-end">
                              {lastCostRate > 0 && (
                                <span className="px-2 py-0.5 text-xs rounded-md font-black bg-teal-100 text-teal-800 border border-teal-300 shadow-2xs" title="Purchase Rate + GST per unit">
                                  Cost: ₹{lastCostRate.toFixed(2)}
                                </span>
                              )}
                              {lastRate > 0 && (
                                <span className="px-1.5 py-0.5 text-[11px] font-bold text-slate-600 bg-slate-100 border border-slate-200 rounded">
                                  Rate: ₹{lastRate}
                                </span>
                              )}
                              <span className="px-2 py-0.5 text-[11px] rounded-md font-bold uppercase bg-emerald-100 text-emerald-700">
                                Purchased: {totalPurchased}
                              </span>
                            </div>
                            {selectedSupplier && fromCurrentSupplier > 0 && (
                              <span className="text-[11px] font-bold text-slate-500">
                                From this supplier: {fromCurrentSupplier}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8 opacity-60">
                    <svg className="w-12 h-12 text-slate-300 mb-3 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">No matching products found</p>
                    <p className="text-sm text-slate-400 mt-1 font-bold">Press ENTER to add as a custom product</p>
                  </div>
                )}
             </div>
          </div>
        </div>
        );
      })()}



      {/* Product Details Popup (Like Image) */}
      {activeProductDetailsRow && (() => {
        const row = rows.find(r => r.id === activeProductDetailsRow);
        if (!row) return null;
        
        const qty = parseFloat(row.qty) || 0;
        const rate = parseFloat(row.rate) || 0;
        const discVal = parseFloat(row.disc) || 0;
        const scmDiscAmt = parseFloat(row.scmDiscAmt) || 0;
        const baseAmount = qty * rate;
        let discAmt = 0;
        if (discVal > 0) {
          if (typeof row.disc === 'string' && row.disc.includes('%')) {
            discAmt = (baseAmount * parseFloat(row.disc)) / 100;
          } else {
            discAmt = Math.min(baseAmount, discVal);
          }
        }
        const amount = Math.max(0, baseAmount - discAmt - scmDiscAmt);

        const gstPercent = parseFloat(row.gst) || 0;
        const gstAmt = amount * (gstPercent / 100);
        const amountWithGst = amount + gstAmt;

        const freeQty = parseFloat(row.freeQty) || parseFloat(row.sch) || 0;
        const totalQty = qty + freeQty;
        const costRate = computeRowCostRate(row);

        return (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={() => setActiveProductDetailsRow(null)}>
            <div className="bg-slate-100 border-2 border-[#1e5c5c] shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" onMouseDown={e => e.stopPropagation()}>
               {/* Header */}
               <div className="px-4 py-2 bg-[#1e5c5c] text-white font-black text-sm flex justify-between items-center shadow-sm z-10">
                 <span>Product Details: {row.productName} {row.mfg ? `- ${row.mfg}` : ''}</span>
                 <button onClick={() => setActiveProductDetailsRow(null)} className="hover:text-red-300 text-lg leading-none transition-colors">&times;</button>
               </div>
               
               <div className="p-4 space-y-4 text-xs font-bold text-slate-700 bg-white relative">
                  {/* Top info section */}
                  <div className="grid grid-cols-4 gap-4 px-2 py-2 bg-slate-50 border border-slate-200 rounded">
                    <div className="flex flex-col"><span className="text-slate-400 font-bold uppercase tracking-wider mb-1">Product</span><span className="font-black text-slate-800 text-sm">{row.productName || '-'}</span></div>
                    <div className="flex flex-col"><span className="text-slate-400 font-bold uppercase tracking-wider mb-1">Packing</span><span className="font-black text-slate-800 text-sm">{row.packing || '-'}</span></div>
                    <div className="flex flex-col"><span className="text-slate-400 font-bold uppercase tracking-wider mb-1">Company</span><span className="font-black text-slate-800 text-sm">{row.mfg || 'UNKNOWN'}</span></div>
                    <div className="flex flex-col"><span className="text-slate-400 font-bold uppercase tracking-wider mb-1">Tax category</span><span className="font-black text-slate-800 text-sm">{row.gst ? `${row.gst}% GST` : '0% GST'}</span></div>
                  </div>

                  {/* Main form section matching the image layout roughly */}
                  <div className="flex gap-6 p-2">
                     <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-3">
                       
                       <div className="flex gap-2 items-center col-span-2">
                          <label className="w-20 text-slate-500 text-right">Qty</label>
                          <input type="number" autoFocus value={row.qty} onChange={(e) => updateRow(row.id, 'qty', e.target.value)} className="w-24 border border-teal-400 bg-yellow-100/50 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c] focus:bg-yellow-100" />
                          
                          <label className="w-16 text-slate-500 text-right ml-4">Free / Sch</label>
                          <input type="text" value={row.sch !== undefined && row.sch !== null && row.sch !== '' ? row.sch : (row.freeQty ? String(row.freeQty) : '')} onChange={(e) => updateRow(row.id, 'sch', e.target.value)} className="w-24 border border-slate-300 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c]" placeholder="0" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">MRP</label>
                          <input type="number" value={row.mrp} onChange={(e) => updateRow(row.id, 'mrp', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Purc rate</label>
                          <input type="number" value={row.rate} onChange={(e) => updateRow(row.id, 'rate', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none text-right font-black focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Batch no</label>
                          <input type="text" value={row.batch} onChange={(e) => updateRow(row.id, 'batch', e.target.value)} className="w-32 border border-slate-300 px-2 py-1 rounded outline-none uppercase focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Expiry dt.</label>
                          <input type="text" value={row.exp} onChange={(e) => {
                            let val = e.target.value.replace(/[^\d]/g, '');
                            if (val.length > 2) val = val.substring(0, 2) + '/' + val.substring(2, 4);
                            updateRow(row.id, 'exp', val);
                          }} placeholder="MM/YY" maxLength={5} className="w-24 border border-slate-300 px-2 py-1 rounded outline-none text-center uppercase focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">Disc (₹)</label>
                          <input type="number" value={row.disc} onChange={(e) => updateRow(row.id, 'disc', e.target.value)} className="w-24 border border-slate-300 px-2 py-1 rounded outline-none text-right focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center">
                          <label className="w-20 text-slate-500 text-right">GST %</label>
                          <input type="number" value={row.gst} onChange={(e) => updateRow(row.id, 'gst', e.target.value)} className="w-24 border border-slate-300 px-2 py-1 rounded outline-none text-right focus:border-[#1e5c5c]" />
                       </div>
                       
                       <div className="flex gap-2 items-center mt-2 col-span-2">
                          <label className="w-24 text-slate-500 font-bold text-right">Taxable Amt</label>
                          <input type="text" readOnly value={`₹ ${amount.toFixed(2)}`} className="w-36 border border-slate-200 bg-slate-100 text-[#1e5c5c] px-2 py-1 rounded outline-none text-right font-black" />
                          <span className="text-[10px] text-slate-400 font-bold">(Excl. GST)</span>
                       </div>

                       <div className="flex gap-2 items-center col-span-2">
                          <label className="w-24 text-emerald-700 font-black text-right">Net Amt (GST)</label>
                          <input type="text" readOnly value={`₹ ${amountWithGst.toFixed(2)}`} className="w-36 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 px-2 py-1 rounded outline-none text-right font-black text-sm shadow-xs" />
                          <span className="text-[10px] text-emerald-600 font-bold">(GST धरून एकूण)</span>
                       </div>
                       
                       <div className="flex gap-2 items-center col-span-2">
                          <label className="w-24 text-teal-800 font-black text-right">Cost rate</label>
                          <input type="text" readOnly value={`₹ ${costRate.toFixed(2)}`} className="w-36 border-2 border-teal-500 bg-teal-50 text-teal-800 px-2 py-1 rounded outline-none text-right font-black shadow-xs" />
                          <span className="text-[10px] text-teal-600 font-bold">(Rate + GST धरून प्रति युनिट)</span>
                       </div>
                     </div>
                     
                     {/* Right panels */}
                     <div className="w-[350px] flex flex-col gap-4">
                        {/* Taxes box */}
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 relative pt-4 shadow-xs">
                          <span className="absolute -top-2 left-4 bg-white px-2 border border-slate-200 rounded text-slate-500 text-[10px] uppercase tracking-widest font-black">Tax Breakup</span>
                          <div className="space-y-1.5 text-xs font-bold mt-1">
                             <div className="flex justify-between border-b border-slate-200 pb-1">
                               <span className="text-slate-500">Taxable Base</span>
                               <span className="font-black text-slate-700">₹{amount.toFixed(2)}</span>
                             </div>
                             <div className="flex justify-between border-b border-slate-200 pb-1">
                               <span className="text-slate-500">CGST ({(gstPercent / 2).toFixed(1)}%)</span>
                               <span className="font-black text-slate-700">₹{(gstAmt / 2).toFixed(2)}</span>
                             </div>
                             <div className="flex justify-between border-b border-slate-200 pb-1">
                               <span className="text-slate-500">SGST ({(gstPercent / 2).toFixed(1)}%)</span>
                               <span className="font-black text-slate-700">₹{(gstAmt / 2).toFixed(2)}</span>
                             </div>
                             <div className="flex justify-between border-b border-slate-200 pb-1 text-[#1e5c5c]">
                               <span>Total GST ({gstPercent}%)</span>
                               <span className="font-black">₹{gstAmt.toFixed(2)}</span>
                             </div>
                             <div className="flex justify-between pt-1.5 text-emerald-800 text-xs font-black bg-emerald-100/70 p-2 rounded-lg border border-emerald-300">
                               <span>Final Amt (GST धरून):</span>
                               <span>₹{amountWithGst.toFixed(2)}</span>
                             </div>
                          </div>
                        </div>

                        {/* Past History box */}
                        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 relative pt-4 flex-1 flex flex-col min-h-[140px]">
                          <span className="absolute -top-2 left-4 bg-white px-2 border border-slate-200 rounded text-slate-400 text-[10px] uppercase tracking-widest font-black">Last Purchases</span>
                          <div className="mt-1 text-[11px] flex-1 overflow-y-auto">
                            <table className="w-full text-left border-collapse">
                              <thead className="text-slate-400 border-b border-slate-200">
                                <tr>
                                  <th className="pb-1 font-bold">Supplier</th>
                                  <th className="pb-1 text-right font-bold">MRP</th>
                                  <th className="pb-1 text-right font-bold">Rate</th>
                                  <th className="pb-1 text-center font-bold">Qty+Free</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {allPurchases && allPurchases.flatMap(bill => {
                                   return (bill.items || []).filter(item => item.productName?.toUpperCase() === row.productName?.toUpperCase()).map((item, idx) => (
                                     <tr key={`${bill.id}-${idx}`} className="hover:bg-slate-100">
                                       <td className="py-1 max-w-[100px] truncate" title={bill.supplierName}>{bill.supplierName}</td>
                                       <td className="py-1 text-right font-black">{item.mrp}</td>
                                       <td className="py-1 text-right font-black text-[#1e5c5c]">{item.rate}</td>
                                       <td className="py-1 text-center font-black">{item.qty}</td>
                                     </tr>
                                   ))
                                })}
                                {(!allPurchases || !allPurchases.some(bill => (bill.items||[]).some(it => it.productName?.toUpperCase() === row.productName?.toUpperCase()))) && (
                                   <tr>
                                     <td colSpan="4" className="py-4 text-center text-slate-400 font-bold italic">No past history found.</td>
                                   </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                     </div>
                  </div>
               </div>
               
               {/* Buttons */}
               <div className="flex justify-end gap-3 px-4 py-3 bg-slate-100 border-t border-slate-200">
                  <button onClick={() => setActiveProductDetailsRow(null)} className="px-6 py-1.5 bg-slate-200 text-slate-600 rounded-lg shadow-sm hover:bg-slate-300 transition-colors font-black uppercase tracking-widest text-xs">Cancel</button>
                  <button onClick={() => setActiveProductDetailsRow(null)} className="px-8 py-1.5 bg-[#1e5c5c] text-white rounded-lg shadow-md hover:bg-[#154242] transition-colors font-black uppercase tracking-widest text-xs">Ok</button>
               </div>
            </div>
          </div>
        );
      })()}



      {/* Document Image & Extraction Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white border-2 border-[#1e5c5c] shadow-2xl rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-3.5 bg-gradient-to-r from-[#1e5c5c] to-teal-800 text-white font-black text-sm flex justify-between items-center shadow-md shrink-0">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                <span>📷 Uploaded Bill & AI Extraction</span>
              </div>
              <button 
                onClick={() => setShowImageModal(false)}
                className="hover:bg-teal-700/50 p-1 rounded-full text-white/80 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Content Area: Image Left, Extracted Data Right */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left Side: Uploaded Bill Image Preview */}
              <div className="w-1/2 p-4 bg-slate-100 border-r border-slate-200 flex flex-col items-center justify-between relative overflow-hidden">
                <div className="flex gap-2 mb-2 w-full justify-center shrink-0">
                  <button 
                    onClick={() => {
                      const next = (currentRotation + 90) % 360;
                      setCurrentRotation(next);
                      if (currentFile) handleExtractInvoice(currentFile, next);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-xs rounded-xl shadow-xs flex items-center gap-1.5 transition-all"
                    title="Rotate 90° Clockwise and Re-Scan"
                  >
                    🔄 Rotate 90° CW
                  </button>
                  <button 
                    onClick={() => {
                      const next = (currentRotation + 270) % 360;
                      setCurrentRotation(next);
                      if (currentFile) handleExtractInvoice(currentFile, next);
                    }}
                    className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-xs rounded-xl shadow-xs flex items-center gap-1.5 transition-all"
                    title="Rotate 90° Counter-Clockwise and Re-Scan"
                  >
                    🔄 Rotate 90° CCW
                  </button>
                </div>

                <div className="flex-1 flex items-center justify-center overflow-hidden w-full">
                  {uploadedDoc ? (
                    <img 
                      src={uploadedDoc} 
                      alt="Uploaded Bill" 
                      className="max-h-full max-w-full object-contain rounded-lg shadow-md border border-slate-300 transition-all duration-300"
                    />
                  ) : (
                    <p className="text-xs font-bold text-slate-400">No Image Uploaded</p>
                  )}
                </div>
              </div>

              {/* Right Side: Extraction Progress & Results */}
              <div className="w-1/2 p-5 flex flex-col justify-between overflow-y-auto bg-white">
                <div>
                  {isExtracting ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                      <div className="w-12 h-12 border-4 border-[#1e5c5c] border-t-transparent rounded-full animate-spin"></div>
                      <p className="font-black text-slate-800 text-base">{extractionStatus}</p>
                      <p className="text-xs text-slate-400 font-bold max-w-sm">Scanning products, rates, discounts, taxes, less amount, and grand total...</p>
                    </div>
                  ) : extractedData ? (
                    <div className="space-y-3 text-xs font-bold">
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl">
                        <div className="flex items-center justify-between">
                          <p className="text-teal-900 font-black text-sm">✨ Bill Photo Matched Details</p>
                          <span className="px-2 py-0.5 bg-teal-200/60 text-teal-900 rounded font-black text-[10px] uppercase">
                            AI Verified
                          </span>
                        </div>
                        <p className="text-slate-500 font-medium text-[11px] mt-0.5">Every row, discount, tax, and deduction extracted directly from the bill photo.</p>
                      </div>

                      {/* Header Info */}
                      <div className="grid grid-cols-3 gap-2 p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                        <div>
                          <label className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Supplier Name</label>
                          <div className="font-black text-slate-800 text-xs truncate mt-0.5" title={extractedData.supplierName}>
                            {extractedData.supplierName || selectedSupplier?.name || 'Walk-in'}
                          </div>
                        </div>
                        <div>
                          <label className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Invoice No</label>
                          <div className="font-black text-slate-800 text-xs mt-0.5">
                            {extractedData.invoiceNo || invoiceNo || '-'}
                          </div>
                        </div>
                        <div>
                          <label className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Date</label>
                          <div className="font-black text-slate-800 text-xs mt-0.5">
                            {extractedData.date || date}
                          </div>
                        </div>
                      </div>

                      {/* Prominent Financial Breakdown Matching Bill Layout */}
                      <div className="p-3 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 text-white rounded-xl shadow-md border border-teal-500/30 space-y-2">
                        <div className="flex items-center justify-between pb-1.5 border-b border-white/10">
                          <span className="text-[11px] font-black uppercase tracking-wider text-teal-300">
                            🧾 Bill Financial Breakdown (हिशोब)
                          </span>
                          <span className="text-[10px] text-teal-100/70 font-semibold">Image Match</span>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          {/* Gross Amount */}
                          <div className="bg-white/5 p-2 rounded-lg border border-white/10">
                            <div className="text-[9px] uppercase font-bold text-slate-400">Gross Total (एकूण मूळ)</div>
                            <div className="text-sm font-black text-white mt-0.5">
                              ₹{(extractedData.grossAmount || grossAmount).toFixed(2)}
                            </div>
                          </div>

                          {/* Total Discount */}
                          <div className="bg-rose-500/10 p-2 rounded-lg border border-rose-500/20">
                            <div className="text-[9px] uppercase font-bold text-rose-300 flex justify-between">
                              <span>Discount (सूट)</span>
                              {parseFloat(extractedData.cashDiscPercent || cashDiscPercent) > 0 && (
                                <span className="text-[8px] bg-rose-500/30 px-1 rounded">{extractedData.cashDiscPercent || cashDiscPercent}%</span>
                              )}
                            </div>
                            <div className="text-sm font-black text-rose-400 mt-0.5">
                              -₹{(parseFloat(extractedData.lessAmount || lessAmount) > 0 ? parseFloat(extractedData.lessAmount || lessAmount) : ((parseFloat(extractedData.cashDisc) || parseFloat(cashDisc) || 0) + (parseFloat(extractedData.itemDisc) || parseFloat(itemDisc) || 0) + (parseFloat(extractedData.schDisc) || parseFloat(schDisc) || 0))).toFixed(2)}
                            </div>
                            <div className="text-[8px] text-slate-400">{parseFloat(extractedData.lessAmount || lessAmount) > 0 ? 'Less Disc' : 'Item+Cash+Sch'}</div>
                          </div>

                          {/* Less Amount */}
                          <div className="bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
                            <div className="text-[9px] uppercase font-bold text-amber-300">Less (कमी रक्कम)</div>
                            <div className="text-sm font-black text-amber-400 mt-0.5">
                              -₹{(parseFloat(extractedData.lessAmount || lessAmount) || 0).toFixed(2)}
                            </div>
                            <div className="text-[8px] text-slate-400">Deductions</div>
                          </div>

                          {/* Taxable Amount */}
                          <div className="bg-blue-500/10 p-2 rounded-lg border border-blue-500/20">
                            <div className="text-[9px] uppercase font-bold text-blue-300">Taxable (करपात्र)</div>
                            <div className="text-sm font-black text-blue-300 mt-0.5">
                              ₹{(extractedData.taxableAmount || taxableAmount).toFixed(2)}
                            </div>
                            <div className="text-[8px] text-slate-400">Base Value</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/10 text-xs">
                          {/* Total Tax (GST) */}
                          <div className="bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/20">
                            <div className="text-[9px] uppercase font-bold text-emerald-300">Total Tax (GST / कर)</div>
                            <div className="text-sm font-black text-emerald-400 mt-0.5">
                              +₹{(extractedData.totalTax !== undefined && extractedData.totalTax !== null ? parseFloat(extractedData.totalTax) : totalTaxAmount).toFixed(2)}
                            </div>
                            <div className="text-[8px] text-slate-400">
                              {extractedData.cgstAmount > 0 ? `CGST: ₹${extractedData.cgstAmount} | SGST: ₹${extractedData.sgstAmount}` : (totalTaxAmount > 0 ? 'GST Applied' : '0% GST / No Tax')}
                            </div>
                          </div>

                          {/* Round Off */}
                          <div className="bg-white/5 p-2 rounded-lg border border-white/10">
                            <div className="text-[9px] uppercase font-bold text-slate-400">Round Off (राउंड)</div>
                            <div className="text-sm font-black text-slate-300 mt-0.5">
                              {parseFloat(extractedData.roundOff || roundOff) >= 0 ? `+₹${(parseFloat(extractedData.roundOff || roundOff) || 0).toFixed(2)}` : `-₹${Math.abs(parseFloat(extractedData.roundOff || roundOff) || 0).toFixed(2)}`}
                            </div>
                          </div>

                          {/* Net Bill Total */}
                          <div className="bg-gradient-to-r from-teal-500 to-emerald-600 p-2 rounded-lg shadow-md border border-teal-300/40">
                            <div className="text-[9px] uppercase font-black text-teal-100">Bill Total (एकूण देय)</div>
                            <div className="text-base font-black text-white mt-0.5">
                              ₹{(parseFloat(extractedData.netAmount) || toPay).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Items List */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <h4 className="font-black text-slate-700 text-xs uppercase tracking-wider">
                            Line Items Breakdown ({extractedData.items?.length || validRows.length || 0})
                          </h4>
                          <span className="text-[10px] text-slate-500 font-bold">
                            Total Qty: {(extractedData.items || []).reduce((s, it) => s + (parseFloat(it.qty) || 0), 0) || totalQty}
                          </span>
                        </div>
                        <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100 text-[11px]">
                          {(extractedData.items || validRows).map((it, idx) => (
                            <div key={idx} className="p-2 hover:bg-slate-50 flex justify-between items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-slate-400 font-bold text-[10px]">#{idx + 1}</span>
                                  <p className="font-black text-slate-800 truncate">{it.productName}</p>
                                  {it.packing && (
                                    <span className="px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-bold">
                                      {it.packing}
                                    </span>
                                  )}
                                  {it.mfg && (
                                    <span className="text-[9px] text-slate-400 font-normal">({it.mfg})</span>
                                  )}
                                </div>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                  Batch: <span className="font-bold text-slate-700">{it.batch || '-'}</span> | Exp: {it.exp || '-'} | Rate: ₹{it.rate} | MRP: ₹{it.mrp} | GST: {it.gst}%
                                  <span className="text-teal-700 font-bold ml-1.5 bg-teal-50 px-1 rounded">Cost Rate: ₹{computeRowCostRate(it).toFixed(2)}</span>
                                  {parseFloat(it.disc) > 0 && <span className="text-rose-600 font-bold ml-1">Disc: ₹{it.disc}</span>}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-black text-emerald-700">₹{(parseFloat(it.amount) || ((parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0))).toFixed(2)}</p>
                                <p className="text-[10px] text-slate-400">{it.qty} Qty {(parseFloat(it.freeQty || it.free || it.sch || 0) > 0 || (it.sch && it.sch !== '0' && it.sch !== '')) ? `+ ${it.sch || it.freeQty || it.free} Free/Sch` : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-slate-400 font-bold">
                      <p>No extraction data available for this image.</p>
                    </div>
                  )}
                </div>

                {/* Bottom Modal Actions */}
                <div className="pt-4 border-t border-slate-200 flex justify-end items-center">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShowImageModal(false)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-xs uppercase"
                    >
                      Close Preview
                    </button>
                    {extractedData && !isExtracting && (
                      <button 
                        onClick={applyExtractedData}
                        className="px-6 py-2 bg-[#1e5c5c] hover:bg-[#154242] text-white rounded-xl font-black text-xs uppercase shadow-md transition-all flex items-center gap-2"
                      >
                        <span>✨ Re-Apply Data to Form</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gemini API Key Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-600 to-amber-700 text-white">
              <div className="flex items-center gap-2">
                <span className="text-lg">🔑</span>
                <h3 className="text-sm font-black">Set Gemini AI API Key</h3>
              </div>
              <button onClick={() => setShowApiKeyModal(false)} className="p-1 text-white/80 hover:text-white rounded-full">
                ✕
              </button>
            </div>
            
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600 font-medium leading-relaxed">
                Enter or update the Gemini API Key for bill scanning on this laptop. Get your free key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="text-amber-700 underline font-bold">aistudio.google.com</a>.
              </p>
              
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Gemini API Key</label>
                <input 
                  type="text" 
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Paste AIzaSy... key here"
                  className="w-full bg-slate-50 border border-slate-200 text-slate-900 text-xs font-mono font-bold rounded-xl px-3 py-2 outline-none focus:border-amber-600 focus:bg-white"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <button 
                  onClick={async () => {
                    localStorage.removeItem('gemini_api_key');
                    setApiKeyInput('');
                    try {
                      await fetch(apiUrl('/api/settings/gemini-key'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ apiKey: 'AQ.Ab8RN6JPZCyMbnUvHsH1z2Qp4GFAgJSvgniJVmXIz8Ac28gTzA' })
                      });
                    } catch (e) {}
                    alert('Reset to system permanent API key!');
                  }}
                  className="text-xs text-rose-600 font-bold hover:underline"
                >
                  Reset to Default
                </button>

                <div className="flex gap-2">
                  <button 
                    onClick={() => setShowApiKeyModal(false)}
                    className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={async () => {
                      if (apiKeyInput.trim()) {
                        const trimmed = apiKeyInput.trim();
                        localStorage.setItem('gemini_api_key', trimmed);
                        try {
                          await fetch(apiUrl('/api/settings/gemini-key'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ apiKey: trimmed })
                          });
                        } catch (e) {
                          console.warn('Backend save notice:', e);
                        }
                        alert('✨ API Key saved permanently for this laptop and account!');
                        setShowApiKeyModal(false);
                      } else {
                        alert('Please paste a valid API key.');
                      }
                    }}
                    className="px-4 py-1.5 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg shadow-sm"
                  >
                    Save Key
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Purchases;
